import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    CUSTOM_ELEMENTS_SCHEMA,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WebsiteBuilderService } from '../../../services/website-builder/website-builder.service';
import { WbAiChatbotStatusCardComponent } from '../../app-layout/website-builder/wb-ai-chatbot-status-card/wb-ai-chatbot-status-card.component';
import { fromEvent, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter as rxFilter, switchMap } from 'rxjs/operators';
import moment from 'moment-timezone';

import { GableReloadRouteReuseStrategy } from '../../../services/gable/gable-reload-route-reuse.strategy';
import { JwtLegFiClaims } from '../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import {
    gableAttachmentKey,
    GableChatAttachment,
    GableChatMessage,
    GableDrawerService,
    GableMode,
    GableThought,
} from '../../../services/gable/gable-drawer.service';
import { attachmentIconClass, GableAttachmentUploaderService } from '../../../services/gable/gable-attachment-uploader.service';
import { GableAudioLevelsController } from '../dictation/gable-audio-levels';
import { DocumentsService } from '../../../services/communication/documents.service';
import { OrgContextService } from '../../../services/organization/org-context.service';
import { OrganizationFile } from '../../../models/entities/organization-file';
import {
    GableDictationController,
    getRecognitionCtor,
} from '../dictation/gable-dictation.controller';
import {
    GablePreference,
    GablePreferenceSource,
    GablePreferencesService,
} from '../../../services/gable/gable-preferences.service';
import {
    ActionPreviewCardPayload,
    ActionPreviewEdits,
    GableActivitySummaryItem,
    GableCitation,
    GableConversationScope,
    GableConversationScopeRef,
    GableConversationSummary,
    GableFeedback,
    GableFeedbackChip,
    GablePendingProposal,
    GableQuestionAnswer,
    GableToolCard,
    GableService,
    BudgetRedirectCardPayload,
    DeliverableCardPayload,
    GenerateReportCardPayload,
    LedgerIntelligenceResult,
    isActionPreviewCardPayload,
    isBudgetRedirectCardPayload,
    isRedirectCardTool,
    isDeliverableCardPayload,
    isDeliverableCardTool,
    isGenerateReportCardPayload,
    isLedgerIntelligenceCardPayload,
    gableCommitFailureMessage,
    isGableSkippedRefusal,
    isGableTerminalRefusal,
} from '../../../services/gable/gable.service';
import { messageCarriesProposal } from './message-carries-proposal';
import { PageContextService } from '../../../services/gable/page-context.service';
import { GableDrillDownOrg, GableScopeService } from '../../../services/gable/gable-scope.service';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';
import { PmcOrganizationsService } from '../../../services/property-management-company/pmc-organizations.service';
import { Organization } from '../../../models/entities/organization';
import { LocalService } from '../../../services/storage/local.service';
import { GablePendingQuestions } from '../../../services/gable/gable-stream.service';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import {
    ApprovalDialogComponent,
    ApprovalDialogResult,
} from '../../v2-layout/gable/approval-dialog/approval-dialog.component';
import { GrowlerService } from '../../shared/growler/growler.service';
import { QuestionCardComponent } from '../../v2-layout/gable/question-card/question-card.component';
import { GableBudgetRedirectCardComponent } from '../../v2-layout/gable/budget-redirect-card/budget-redirect-card.component';
import { GableActionPreviewCardComponent } from '../../v2-layout/gable/action-preview/action-preview-card.component';
import { GableReportCardComponent } from '../../v2-layout/gable/report-card/report-card.component';
import { GableDeliverableCardComponent } from '../../v2-layout/gable/deliverable-card/deliverable-card.component';
import { GableLedgerIntelligenceCardComponent } from '../../v2-layout/gable/ledger-intelligence-card/ledger-intelligence-card.component';
import { GableMatchState, GableMessageHighlightDirective } from './gable-message-highlight.directive';
import { GableFeedbackPanelComponent } from '../gable-feedback-panel/gable-feedback-panel.component';
import { campaignAsksFor, FEEDBACK_CAMPAIGN_ENDS_AT, GableFeedbackAskMemory, threadKey } from './gable-feedback-ask';
import { GableActivityPanelComponent } from '../gable-activity-panel/gable-activity-panel.component';
import { GABLE_HERO } from '../gable-autopilot-mock';
import {
    GableActivityFilter,
    MODULE_LABELS,
    moduleForActionType,
} from '../gable-activity-panel/gable-activity-panel.component';
import {
    GableSuggestionCardComponent,
    GableSuggestionCardPayload,
} from '../gable-suggestion-card/gable-suggestion-card.component';
import { GableSuggestionsPipe } from '../../../pipes/gable-suggestions.pipe';
import { GableSkillsPanelComponent } from '../gable-skills-panel/gable-skills-panel.component';
import { GableWorkflowsPanelComponent } from '../gable-workflows-panel/gable-workflows-panel.component';
import { IntercomService } from '../../../services/intercom.service';
import { ConfirmDialogComponent } from '../../v2-layout/shared/dialogs/confirm-dialog/confirm-dialog.component';

/** Composer auto-grow cap, in rows — past this the textarea scrolls internally. */
const COMPOSER_MAX_ROWS = 8;

const AFFIRMATIONS = new Set(['yes', 'y', 'confirm', 'confirmed', 'go', 'go ahead', 'do it', 'ok', 'okay', 'yes please', 'approve']);

const DECLINE_PATTERN = /\b(I can['’]?t|I cannot|isn['’]?t something I can|not able to)\b/i;

const ATTENTION_MS = 1600;

const NO_THOUGHTS: GableThought[] = [];

type GableReasoningStep = { label: string; narration?: boolean };

type GableComposerElement = HTMLElement & { levels?: number[]; value?: string };

const REASONING_STEPS = new WeakMap<GableThought[], GableReasoningStep[]>();

const PAYLOAD_VERDICTS = new WeakMap<object, boolean>();

/** One chip in `<ph-composer>`: kit `ComposerAttachment`. */
interface GableComposerAttachment {
    id: string;
    name: string;
    meta?: string;
    kind?: 'file' | 'image';
    status?: 'uploading' | 'error';
    previewUrl?: string;
    error?: string;
    documentId?: string;
}

/** One entry in the composer's document picker: kit `PromptArea.Document`. */
interface GableLibraryDocument {
    id: string;
    name: string;
    category?: string;
    meta?: string;
}

const PICKER_CATEGORY_LIMIT = 8;

function topCategories(docs: GableLibraryDocument[], limit: number): string[] {
    const counts = new Map<string, number>();
    docs.forEach((d) => d.category && counts.set(d.category, (counts.get(d.category) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([name]) => name);
}

function toLibraryDocuments(files: OrganizationFile[]): GableLibraryDocument[] {
    const folders = new Map(files.filter((f) => f.directory).map((f) => [f.id, f.fileName]));
    return files
        .filter((f) => !f.directory)
        .sort((a, b) => (b.updatedAt?.valueOf() ?? 0) - (a.updatedAt?.valueOf() ?? 0))
        .map((f) => ({
            id: String(f.id),
            name: f.fileName,
            category: folders.get(f.parentId) ?? 'Documents',
            meta: f.updatedAt ? `Updated ${f.updatedAt.format('MMM D, YYYY')}` : undefined,
        }));
}
/**
 * Character cap on the chat draft. Bound to the textarea's `maxlength` AND
 * handed to the dictation controller: the attribute only constrains the
 * keyboard, and a dictated commit would otherwise walk straight past it.
 * The counter's copy in the template (`9000`, `10,000`) is the same number.
 */
const COMPOSER_MAX_LENGTH = 10000;

/** Character cap for both Instructions boxes — matches core's `max:500` on every write path. */
const PREFS_MAX_CHARS = 500;
/** Up to this many instructions are read at a glance, so the search row stays hidden. */
const PREFS_SEARCH_MIN_ROWS = 5;

/** Which panel the drawer body renders. `archived` is expanded-mode only. */
type GableDrawerTab = 'chat' | 'activity' | 'workflows' | 'skills' | 'prefs' | 'archived' | 'ai-settings';

/**
 * Sort options for the Archived chats panel. Client-side: the conversation list
 * endpoint always orders by `last_message_at DESC` and takes no sort param.
 */
type GableArchivedSort = 'recent' | 'oldest' | 'title';

/**
 * Below this the expanded dialog can't hold its own layout: the rail is a fixed
 * 280px and the dialog is 92vw, so 92vw must cover 280 (rail) + 48 (column
 * padding) + ~560 (a usable measure) ≈ 888px → ~960px of viewport. Narrower than
 * that and the conversation column breaks words mid-syllable, so expanded falls
 * back to the drawer layout, which is built for exactly this width.
 */
const EXPANDED_MIN_VIEWPORT = '(max-width: 959px)';

/** `ph-select` cannot carry an empty value, so "All" rides on this sentinel. */
const FILTER_ALL = '__all';

/** Result of a copy-button press while its confirmation is on screen. */
type GableCopyState = 'copied' | 'failed';

interface GableQuickAction {
    // 'page' is dynamic — only present when the server returns page-aware
    // starter suggestions for the current route (see loadWelcomeSuggestions).
    id: 'page' | 'popular' | 'finance' | 'reports' | 'units' | 'violations' | 'requests' | 'comms';
    label: string;
    icon: string | null;
}

interface GableSuggestion {
    label: string;
    prompt: string;
}

/**
 * Per-module card presentation for the empty-state grid. Add a module here and it
 * renders automatically once it has pending items — nothing else to touch.
 *
 * `tone` keys the icon-tile tint in SCSS; the palette is the Autopilot mock's.
 */
export const GABLE_MODULE_CARDS: Record<
    Exclude<GableActivityFilter, 'all'>,
    { icon: string; tone: string; tagline: string }
> = {
    requests: {
        icon: 'ph-wrench',
        tone: 'cyan',
        tagline: 'Triage and reply with suggested decisions',
    },
    violations: {
        icon: 'ph-scales',
        tone: 'orange',
        tagline: 'Reminders, escalations and fines to approve',
    },
    payments: {
        icon: 'ph-bank',
        tone: 'green',
        tagline: 'Match deposits, categorize, flag anything odd',
    },
    communications: {
        icon: 'ph-chat-circle-text',
        tone: 'indigo',
        tagline: 'Drafted messages waiting to send',
    },
    amenities: {
        icon: 'ph-swimming-pool',
        tone: 'cyan',
        tagline: 'Booking approvals and cancellations',
    },
    calendar: {
        icon: 'ph-calendar-check',
        tone: 'indigo',
        tagline: 'Events ready to add to the calendar',
    },
    surveys: {
        icon: 'ph-clipboard-text',
        tone: 'green',
        tagline: 'Sends and closes waiting on you',
    },
    voting: {
        icon: 'ph-checks',
        tone: 'orange',
        tagline: 'Ballot reminders and results to share',
    },
};

/**
 * Omnipresent Gable AI drawer — slides in from the right edge over the
 * router-outlet on every authenticated route. Thin view over
 * `GableDrawerService` (which owns conversation, streaming, width, etc.).
 *
 * Layout:
 *   [ header — title, dock-toggle, close ]
 *   [ scrollable body — empty-state OR conversation OR pending approvals ]
 *   [ composer — textarea + send button ]
 */
@UntilDestroy()
@Component({
    selector: 'app-gable-drawer-host',
    templateUrl: './gable-drawer-host.component.html',
    styleUrls: ['./gable-drawer-host.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        WbAiChatbotStatusCardComponent,
        FormsModule,
        ScrollingModule,
        ConfirmDialogComponent,
        ApprovalDialogComponent,
        GableActionPreviewCardComponent,
        GableBudgetRedirectCardComponent,
        GableMessageHighlightDirective,
        GableFeedbackPanelComponent,
        GableReportCardComponent,
        GableDeliverableCardComponent,
        GableLedgerIntelligenceCardComponent,
        GableActivityPanelComponent,
        GableWorkflowsPanelComponent,
        GableSkillsPanelComponent,
        GableSuggestionCardComponent,
        GableSuggestionsPipe,
        HighlightPipe,
        QuestionCardComponent,
    ],
})
export class GableDrawerHostComponent implements OnInit, OnDestroy, AfterViewChecked {
    /** The `<ph-composer>`; `levels` and `value` are written straight onto it. */
    @ViewChild('messageInput') set messageInputRef(ref: ElementRef<GableComposerElement> | undefined) {
        const previous = this._composerRef?.nativeElement;
        this._composerRef = ref;
        if (previous === ref?.nativeElement) {
            return;
        }
        previous?.removeEventListener('ph-change', this._onComposerInput);
        ref?.nativeElement.addEventListener('ph-change', this._onComposerInput);
    }
    get messageInputRef(): ElementRef<GableComposerElement> | undefined {
        return this._composerRef;
    }
    private _composerRef?: ElementRef<GableComposerElement>;
    /** Bound natively: a template listener would dirty this whole OnPush host on every keystroke. */
    private readonly _onComposerInput = (event: Event): void => this.onDraftChange((event as CustomEvent<string>).detail);
    @ViewChild('archivedSearchInput') archivedSearchInputRef?: ElementRef<HTMLElement>;
    @ViewChild('prefsAddInput') prefsAddInputRef?: ElementRef<HTMLElement>;
    @ViewChild('prefsSearchInput') prefsSearchInputRef?: ElementRef<HTMLElement>;
    @ViewChild('prefsEditInput') prefsEditInputRef?: ElementRef<HTMLElement>;
    @ViewChild('prefsAddTrigger', { read: ElementRef }) prefsAddTriggerRef?: ElementRef<HTMLElement>;
    /** Undefined unless the Archived panel is the open tab. */
    @ViewChild('archivedViewport') archivedViewport?: CdkVirtualScrollViewport;
    /** Undefined whenever the thread isn't rendered (empty state, other tabs). */
    @ViewChild(GableMessageHighlightDirective) messageHighlight?: GableMessageHighlightDirective;
    @ViewChild('attachmentFileInput') attachmentFileInputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('attachmentImageInput') attachmentImageInputRef?: ElementRef<HTMLInputElement>;
    @ViewChild('communityResultsList', { read: ElementRef }) communityResultsRef?: ElementRef<HTMLElement>;
    @ViewChild('conversationScroller') conversationScrollerRef?: ElementRef<HTMLElement>;
    @ViewChildren(GableActionPreviewCardComponent)
    actionPreviewCardsRef?: QueryList<GableActionPreviewCardComponent>;

    /** Quick-action chip strip — drives the suggestion list rendered below. */
    readonly quickActions: ReadonlyArray<GableQuickAction> = [
        { id: 'popular', label: 'Popular', icon: 'ph-trend-up' },
        { id: 'finance', label: 'Finance', icon: 'ph-currency-circle-dollar' },
        { id: 'reports', label: 'Reports', icon: 'ph-file-text' },
        { id: 'units', label: 'Units', icon: 'ph-house-line' },
        { id: 'violations', label: 'Violations', icon: 'ph-seal-warning' },
        { id: 'requests', label: 'Requests', icon: 'ph-seal-question' },
        { id: 'comms', label: 'Comms', icon: 'ph-megaphone' },
    ];

    /** Suggestion sets per quick-action chip. */
    private readonly _suggestionsByAction: Record<Exclude<GableQuickAction['id'], 'page'>, GableSuggestion[]> = {
        popular: [
            { label: 'Show me all open violations', prompt: 'Show me all open violations.' },
            { label: 'Which units have the most violations?', prompt: 'Which units have the most violations?' },
            { label: 'What can you tell me about my organization\'s data?', prompt: 'What can you tell me about my organization\'s data?' },
        ],
        finance: [
            { label: 'Show me this month\'s deposits', prompt: 'Show me this month\'s deposits.' },
            { label: 'Which owners are most past due?', prompt: 'Which owners are most past due?' },
        ],
        reports: [
            { label: 'Generate a delinquency report', prompt: 'Generate a delinquency report for the current period.' },
            { label: 'Generate a violation summary report', prompt: 'Generate a violation summary report.' },
        ],
        units: [
            { label: 'List all units missing an owner', prompt: 'List all units that don\'t currently have an owner assigned.' },
            { label: 'Which units have the most violations?', prompt: 'Which units have the most violations?' },
            { label: 'Show me units with overdue balances', prompt: 'Show me units with overdue balances.' },
        ],
        violations: [
            { label: 'Show me all open violations', prompt: 'Show me all open violations.' },
            { label: 'Which violation categories are most common?', prompt: 'Which violation categories are most common right now?' },
            { label: 'List violations created in the last 30 days', prompt: 'List violations created in the last 30 days.' },
        ],
        requests: [
            { label: 'Show me all open requests', prompt: 'Show me all open architectural and maintenance requests.' },
            { label: 'How many requests have been resolved this month?', prompt: 'How many requests have been resolved this month?' },
        ],
        comms: [
            { label: 'Draft a community-wide reminder about pool hours', prompt: 'Draft a community-wide reminder about pool hours.' },
            { label: 'Send a courtesy warning to specific units', prompt: 'Help me send a courtesy warning to specific units.' },
        ],
    };

    /** Page-aware starter suggestions (Haiku, keyed to the current route).
     *  Empty until loadWelcomeSuggestions() resolves; drives the dynamic
     *  "For this page" chip that fronts the quick-action strip when present. */
    pageSuggestions: GableSuggestion[] = [];
    private _welcomeLoadedForUrl: string | null = null;

    /** PMC name for the portfolio scope banner (null until propertyManagementCompany$ resolves). */
    pmcName: string | null = null;

    // ----- Community picker / drill-down (SD-9209) -----
    communitySearch = '';
    communityResults: Organization[] = [];
    communityLoading = false;
    /** True once a picker search has resolved — gates the "no matches" copy. */
    communitySearched = false;
    private readonly _communitySearch$ = new Subject<string>();

    isOpen = false;
    widthPx = 400;
    mode: GableMode = 'drawer';
    messages: GableChatMessage[] = [];

    /**
     * True once the in-flight turn has told the user it moved to the background — chat is
     * handed back. Read from the turn, not the message row: the row's stream state is what
     * the stall watchdog rewrites, and the composer used to re-lock when it did (SD-9787).
     */
    get backgroundMode(): boolean {
        return this.isSending && this.longRunNotice !== null;
    }

    /** SD-10526: countdown copy for the gable_busy auto-retry notice. */
    get busyNoticeText(): string {
        return `Gable is busy right now. Trying again in ${this.busySecondsRemaining ?? 0}s.`;
    }

    private _wasBackgroundMode = false;

    /**
     * Index of the FIRST assistant message — the only one that shows the
     * Gable mascot. Repeating it on every reply read as a new "Gable is
     * here" each turn; the mascot is an introduction, shown once.
     */
    get firstAssistantIndex(): number {
        return this.messages.findIndex((m) => m.role === 'assistant');
    }
    pendingProposals: GablePendingProposal[] = [];
    /** The pending card a bare "yes" pointed at; pulses briefly (SD-10800). */
    attentionProposalId: number | null = null;
    private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _attentionTimer: ReturnType<typeof setTimeout> | null = null;
    apiSuggestions: string[] = [];
    pendingQuestions: GablePendingQuestions | null = null;
    isSending = false;
    turnInFlightNotice = false;
    longRunNotice: string | null = null;
    connectionLostNotice = false;
    busySecondsRemaining: number | null = null;
    draftMessage = '';

    // ----- Voice dictation -----
    /** Owns the Web Speech session; see `gable-dictation.controller.ts`. */
    readonly dictation: GableDictationController;
    /** Mic amplitude for the waveform — a separate stream, no transcript. */
    private readonly _audioLevels: GableAudioLevelsController;
    readonly composerMaxLength = COMPOSER_MAX_LENGTH;
    readonly composerMaxRows = COMPOSER_MAX_ROWS;
    readonly filterAllValue = FILTER_ALL;

    // ----- Attachments (SD-9309, SD-10482) -----
    pendingAttachments: GableChatAttachment[] = [];
    /** Files still uploading, or that failed; they join `pendingAttachments` once the upload returns an id. */
    attachmentUploads: GableComposerAttachment[] = [];
    /** What `<ph-composer>` shows; rebuilt only when attachments change, never per change-detection pass. */
    composerAttachments: GableComposerAttachment[] = [];
    /** The association's documents for the composer's attach menu and picker. */
    documentLibrary: GableLibraryDocument[] = [];
    /** The picker's filter chips: the biggest folders only; smaller folders' documents browse under Other. */
    documentCategories: string[] = [];
    private readonly _imagePreviews = new Map<number, string>();
    private _uploadSeq = 0;
    private _documentLibraryOrgId: number | null = null;

    // ----- Preferences tab (SD-9302) -----
    preferences: GablePreference[] = [];
    preferencesLoading = false;
    preferencesLoadFailed = false;
    newPreferenceText = '';
    savingPreference = false;
    /**
     * The composer is closed until asked for (SD-9302 follow-up). One add
     * affordance, one editor: opening it cancels an open row edit and opening a
     * row edit closes it, so the panel never shows two boxes competing for the
     * same Save.
     */
    prefsComposerOpen = false;
    prefsSearch = '';
    editingPreferenceId: number | null = null;
    editingPreferenceText = '';
    savingPreferenceEdit = false;
    /** '' = every source. Mirrors `archivedScopeFilter`'s empty-means-all contract. */
    prefsSourceFilter: '' | GablePreferenceSource = '';

    /**
     * Offered in the empty state, where "add an instruction" is an abstract ask
     * — one tap fills the composer so the shape of a good instruction is shown
     * rather than described. Deliberately not the title tooltip's wording: the
     * tooltip says what the page is, these say what a row looks like.
     */
    readonly prefsExamples: string[] = [
        'Always CC the board on violation notices',
        'Never schedule anything on a weekend',
        'Keep replies to three sentences or fewer',
    ];

    // ----- Chat history (SD-6742) -----
    activeConversations: GableConversationSummary[] = [];
    archivedConversations: GableConversationSummary[] = [];
    activeTitle: string | null = null;
    historyLoading = false;
    /** True while a paged active-list fetch (search reset / load-more) runs. */
    conversationsLoading = false;
    /** Server reports another page of recent chats is available. */
    hasMoreConversations = false;
    /** True while a paged archived-list fetch (search reset / load-more) runs. */
    archivedLoading = false;
    /** Server reports another page of archived chats is available. */
    hasMoreArchived = false;
    /** Search term for the Archived chats panel — server-side, its own debounce. */
    archivedSearch = '';
    /** Mirror of the service's archived sort — the menu renders its state from here. */
    archivedSort: GableArchivedSort = 'recent';
    /** Mirror of the service's archived scope filter as a `kind:id` key; '' = all scopes. */
    archivedScopeFilter = '';
    /**
     * True while the header title is a rename input. Separate from
     * `editingConversationId` (the sidebar's inline rename) so renaming from the
     * header doesn't also swap a rail row into edit mode, and vice versa.
     */
    editingHeaderTitle = false;

    /** Working value for the header rename input. */
    headerTitleDraft = '';

    /** Conversation whose title is being edited inline in the sidebar, if any. */
    editingConversationId: number | null = null;
    /** Working value for the inline rename input. */
    editingTitle = '';
    /** Cap the drawer-mode history menu to the N most-recent chats. */
    private static readonly DRAWER_MENU_RECENT_LIMIT = 10;
    /**
     * Search term for the expanded sidebar recent list. Server-side now
     * (SD-6742) — the bound value is debounced into a server call rather
     * than filtering the in-memory list. Kept as a field so the input stays
     * controlled and the empty-state copy can branch on "is a search active".
     */
    historySearch = '';

    /** Drives the "3 / 11" counter next to the rail search field. */
    matchCount = 0;
    matchActiveIndex = -1;
    /** px-from-bottom threshold that triggers the next-page fetch. */
    /**
     * Archived row height, and the virtual viewport's `itemSize`. The two must
     * agree — a fixed-size viewport places rows by index — which is why
     * `.gable-archived-row` pins its height rather than letting content set it.
     */
    readonly archivedRowHeightPx = 77;

    private static readonly INFINITE_SCROLL_THRESHOLD_PX = 80;

    /**
     * Auto-scroll bookkeeping.
     *
     * - When a new user message appears, scroll it to the TOP of the visible
     *   drawer so the user sees "the last thing I asked about" and watches
     *   the response stream in below it.
     * - While an assistant bubble is streaming AND the user hasn't scrolled
     *   up to read history, keep the bottom glued so new tokens stay visible.
     * - If the user scrolls up, stop force-sticking — they're reading. As
     *   soon as they scroll back near the bottom, re-stick.
     */
    private _lastUserMessageIndex = -1;
    private _isPinnedToBottom = true;
    private _lastScrollHeight = 0;
    /** Px-from-bottom under which "pinned" reactivates after the user
     *  scrolls back near the tail. Kept tight so we don't yank from
     *  partway up the history. */
    private static readonly STICK_THRESHOLD_PX = 40;

    /** Tracks which message currently has its inline feedback panel open. */
    private _feedbackPanelOpenFor: number | null = null;

    /** Messages voted on in this session — they get a thank-you, ones voted before a reload don't. */
    private readonly _votedThisSession = new Set<number>();

    /** Answers whose ask has been reported to core this session; core stamps it once anyway. */
    private readonly _askReported = new Set<number>();
    private _askScan: { messages: GableChatMessage[]; key: string } | null = null;
    /** Bumped on every vote: clearing one can bring its ask back on the same thread. */
    private _votesVersion = 0;

    /** Impersonated sessions are support staff, not the person whose feedback we want. */
    private readonly _feedbackCampaignOn = Date.now() < FEEDBACK_CAMPAIGN_ENDS_AT && !LegFiJwtService.read()?.impersonatedBy;

    private readonly _feedbackAskMemory = new GableFeedbackAskMemory(
        LegFiJwtService.read()?.id ? `gable-feedback-ask-${LegFiJwtService.read()?.id}` : null,
    );

    // -------------------------------------------------------------------------
    // Autopilot preview UI (SD-7384 north star — demo surface)
    // -------------------------------------------------------------------------

    /**
     * Kill switch for the Autopilot preview chrome (tabs, hero, status line).
     * Default ON; disable instantly from the console by storing the key
     * 'gable-autopilot-ui' = 'off' + reload if the new chrome misbehaves
     * mid-demo. Chat itself is untouched either way.
     */
    readonly autopilotUi: boolean = LocalService.get('gable-autopilot-ui') !== 'off';

    /** Viewport is under EXPANDED_MIN_VIEWPORT — see the `isExpanded` getter. */
    private _isNarrowViewport = false;

    /** Which drawer tab is showing. Chat is live; Activity renders the
     *  review-queue/feed preview (SD-7969 wires it to real data). No
     *  Autopilot settings tab — suggestions-only per the 2026-07-11
     *  product decision. */
    activeTab: GableDrawerTab = 'chat';

    /** Hero empty-state copy (tagline + status strip). The header status
     *  line, tab badge, and module quick links are all real counts now. */
    readonly apHero = GABLE_HERO;

    /** Module filter handed to the Activity panel (set by module quick links). */
    activityFilter: GableActivityFilter = 'all';

    /** Per-action-type counts behind the hero module quick links. */
    activitySummary: GableActivitySummaryItem[] = [];

    constructor(
        private _drawer: GableDrawerService,
        private _cdr: ChangeDetectorRef,
        private _aiPoc: GableService,
        private _growler: GrowlerService,
        private _pageContext: PageContextService,
        private _router: Router,
        private _websiteBuilder: WebsiteBuilderService,
        private _reuseStrategy: GableReloadRouteReuseStrategy,
        private _scope: GableScopeService,
        private _pmcService: PropertyManagementCompanyService,
        private _pmcOrgs: PmcOrganizationsService,
        private _attachmentUploader: GableAttachmentUploaderService,
        private _documents: DocumentsService,
        private _orgContext: OrgContextService,
        private _preferencesService: GablePreferencesService,
        private _ngZone: NgZone,
        private _intercom: IntercomService,
    ) {
        this.dictation = new GableDictationController(
            {
                // Lazy on purpose: `sendMessage` clears the draft, so a value
                // captured once would go stale and the next dictation would
                // join its first words onto text that is no longer there.
                getValue: () => this.draftMessage ?? '',
                setValue: (next) => this._drawer.setDraft(next),
                onChange: () => {
                    // The single place the meter's stream is synced: silence
                    // timeouts, permission errors and typing-takes-over all end
                    // a session without the component ever being called.
                    this._syncAudioLevels();
                    this._syncComposerValue();
                    this._cdr.markForCheck();
                },
                // Recognition events are not reliably zone-patched; wrapping is
                // idempotent when they are.
                run: (fn) => this._ngZone.run(fn),
                isInAngularZone: () => NgZone.isInAngularZone(),
            },
            getRecognitionCtor(),
            { maxLength: COMPOSER_MAX_LENGTH },
        );
        this._audioLevels = new GableAudioLevelsController(
            (levels) => this._paintWaveform(levels),
            (fn) => this._ngZone.runOutsideAngular(fn),
        );
    }

    ngOnInit(): void {
        this._watchViewportWidth();
        this._router.events
            .pipe(rxFilter((e): e is NavigationEnd => e instanceof NavigationEnd), untilDestroyed(this))
            .subscribe((e) => this._consumeGableDeepLink(e.urlAfterRedirects));
        this._consumeGableDeepLink(this._router.url);

        // A Gable write committed (from the chat card or the Activity panel) →
        // soft-reload the page behind the drawer so it reflects the change,
        // without a hard reload (keeps the shell + drawer + chat mounted).
        // The one-shot reuse-strategy flag forces the routed page to re-init
        // and re-run its own ngOnInit fetch; no per-page wiring. SD-8019.
        this._drawer.actionCommitted$.pipe(untilDestroyed(this)).subscribe(() => {
            const url = this._router.url;
            if (!url || url.startsWith('/auth')) {
                return;
            }
            this._reuseStrategy.setForceReload(true);
            void this._router
                .navigateByUrl(url)
                .finally(() => this._reuseStrategy.setForceReload(false));
        });
        this._drawer.isOpen$.pipe(untilDestroyed(this)).subscribe((open) => {
            this.isOpen = open;
            if (!open) {
                this._stopDictationForHiddenComposer();
            }
            // Opening in the empty state → fetch page-aware starter chips for
            // the current route (best-effort, de-duped per URL).
            if (open && this.isEmptyState) {
                this.loadWelcomeSuggestions();
            }
            if (open) {
                this._loadDocumentLibrary();
            }
            this._cdr.markForCheck();
        });
        // Navigating with the drawer open + empty → refresh the "For this
        // page" chips for the new route (de-duped per URL inside).
        this._pageContext.context$.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.isOpen && this.isEmptyState) {
                this.loadWelcomeSuggestions();
            }
        });
        // Scope boundary crossing (SD-9208): drop stale chips + refetch under the new lane.
        this._scope.scopeKey$.pipe(untilDestroyed(this)).subscribe(() => {
            this._welcomeLoadedForUrl = null;
            this.pageSuggestions = [];
            this._resetCommunityPicker();
            // Hero counts/tiles follow the scope: refetch the summary for the new lane.
            this.activitySummary = [];
            this.loadActivitySummary();
            if (this.isOpen && this.isEmptyState) {
                this.loadWelcomeSuggestions();
            }
            this._cdr.markForCheck();
        });
        // Community picker typeahead (SD-9209) — server search, 25 cap.
        this._communitySearch$
            .pipe(
                debounceTime(250),
                switchMap((term) => {
                    const pmcId = this._scope.pmcId;
                    if (pmcId === null) {
                        return of(null);
                    }
                    this.communityLoading = true;
                    this._cdr.markForCheck();
                    return this._pmcOrgs
                        .getPropertyManagementOrganizations(pmcId, term, 'name', 'asc', 1, 25)
                        .pipe(catchError(() => of(null)));
                }),
                untilDestroyed(this),
            )
            .subscribe((res) => {
                this.communityLoading = false;
                this.communitySearched = res !== null;
                this.communityResults = res?.data ?? [];
                this._cdr.markForCheck();
                if (this.communityResults.length > 0) {
                    this._scrollCommunityResultsIntoView();
                }
            });
        this._pmcService.propertyManagementCompany$.pipe(untilDestroyed(this)).subscribe((pmc) => {
            this.pmcName = pmc?.name ?? null;
            this._cdr.markForCheck();
        });
        this._drawer.widthPx$.pipe(untilDestroyed(this)).subscribe((px) => {
            this.widthPx = px;
            this._cdr.markForCheck();
        });
        this._drawer.mode$.pipe(untilDestroyed(this)).subscribe((m) => {
            this.mode = m;
            this._syncEffectiveMode();
            this._cdr.markForCheck();
        });
        // Programmatic seeds (SD-9233) must land on the chat tab, not a lingering Activity/Skills panel.
        this._drawer.chatViewRequested$.pipe(untilDestroyed(this)).subscribe(() => {
            this.activeTab = 'chat';
            this._cdr.markForCheck();
        });
        this._drawer.activityViewRequested$.pipe(untilDestroyed(this)).subscribe((filter) => {
            this.onSelectTab('activity', filter);
        });
        this._drawer.messages$.pipe(untilDestroyed(this)).subscribe((m) => {
            this.messages = m;
            const bg = this.backgroundMode;
            if (bg && !this._wasBackgroundMode) {
                setTimeout(() => this._focusKitControl(this.messageInputRef?.nativeElement), 60);
            }
            this._wasBackgroundMode = bg;
            this._refreshHighlightAfterMessages();
            this._cdr.markForCheck();
        });
        this._drawer.pendingProposals$.pipe(untilDestroyed(this)).subscribe((p) => {
            this.pendingProposals = p;
            this._cdr.markForCheck();
        });
        this._drawer.pendingQuestions$.pipe(untilDestroyed(this)).subscribe((q) => {
            this.pendingQuestions = q;
            if (q) {
                // The question card replaces the composer — see the footer's `*ngIf`.
                this._stopDictationForHiddenComposer();
            }
            this._cdr.markForCheck();
        });
        this._drawer.isSending$.pipe(untilDestroyed(this)).subscribe((s) => {
            this.isSending = s;
            this._cdr.markForCheck();
        });
        this._drawer.turnInFlightNotice$.pipe(untilDestroyed(this)).subscribe((n) => {
            this.turnInFlightNotice = n;
            this._cdr.markForCheck();
        });
        this._drawer.longRunNotice$.pipe(untilDestroyed(this)).subscribe((n) => {
            this.longRunNotice = n;
            this._cdr.markForCheck();
        });
        this._drawer.connectionLostNotice$.pipe(untilDestroyed(this)).subscribe((n) => {
            this.connectionLostNotice = n;
            this._cdr.markForCheck();
        });
        this._drawer.busySecondsRemaining$.pipe(untilDestroyed(this)).subscribe((n) => {
            this.busySecondsRemaining = n;
            this._cdr.markForCheck();
        });
        this._drawer.draftMessage$.pipe(untilDestroyed(this)).subscribe((d) => this._applyDraft(d));
        this._drawer.pendingAttachments$.pipe(untilDestroyed(this)).subscribe((a) => {
            this.pendingAttachments = a;
            this._releasePreviewsNotIn(a);
            this._refreshComposerAttachments();
        });
        // An in-app org switch keeps this host alive, so the library and any library picks must follow the new org.
        this._orgContext.orgContextChanged$.pipe(untilDestroyed(this)).subscribe((orgId) => {
            if (this._documentLibraryOrgId !== null && orgId !== this._documentLibraryOrgId) {
                this.documentLibrary = [];
                this.documentCategories = [];
                this._documentLibraryOrgId = null;
                this.pendingAttachments.filter((p) => p.documentId).forEach((p) => this._drawer.removePendingAttachment(gableAttachmentKey(p)));
                if (this.isOpen) {
                    this._loadDocumentLibrary();
                }
                this._cdr.markForCheck();
            }
        });

        this._drawer.feedback$.pipe(untilDestroyed(this)).subscribe(() => {
            this._cdr.markForCheck();
        });
        this._drawer.apiSuggestions$.pipe(untilDestroyed(this)).subscribe((s) => {
            this.apiSuggestions = s;
            this._cdr.markForCheck();
        });
        this._drawer.activeConversations$.pipe(untilDestroyed(this)).subscribe((c) => {
            this.activeConversations = c;
            this._cdr.markForCheck();
        });
        this._drawer.archivedConversations$.pipe(untilDestroyed(this)).subscribe((c) => {
            this.archivedConversations = c;
            this._rebuildArchivedView();
            this._cdr.markForCheck();
            // After the rows render: a page that leaves the viewport unfilled
            // would otherwise never scroll, and scrolling is what pages.
            setTimeout(() => this._fillArchivedViewport());
        });
        this._drawer.archivedScopes$.pipe(untilDestroyed(this)).subscribe((scopes) => {
            this._archivedScopes = scopes;
            this._archivedScopeOptions = this._buildArchivedScopeOptions();
            this._cdr.markForCheck();
        });
        this._drawer.activeTitle$.pipe(untilDestroyed(this)).subscribe((t) => {
            this.activeTitle = t;
            this._cdr.markForCheck();
        });
        this._drawer.historyLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.historyLoading = l;
            this._cdr.markForCheck();
        });
        this._drawer.conversationsLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.conversationsLoading = l;
            this._cdr.markForCheck();
        });
        this._drawer.hasMoreConversations$.pipe(untilDestroyed(this)).subscribe((h) => {
            this.hasMoreConversations = h;
            this._cdr.markForCheck();
        });
        this._drawer.archivedLoading$.pipe(untilDestroyed(this)).subscribe((l) => {
            this.archivedLoading = l;
            if (l) {
                this.archivedLoadFailed = false;
            }
            this._cdr.markForCheck();
        });
        this._drawer.hasMoreArchived$.pipe(untilDestroyed(this)).subscribe((h) => {
            this.hasMoreArchived = h;
            this._cdr.markForCheck();
        });
        // The service publishes; the host owns the toast (house convention — the
        // service injects no GrowlerService). Without this a 5xx is rendered as
        // "No archived chats yet", which asserts the archive is empty when it is not.
        this._drawer.archivedError$.pipe(untilDestroyed(this)).subscribe((err) => {
            this.archivedLoadFailed = true;
            this._growler.fromErrorResponse(err);
            this._cdr.markForCheck();
        });
        // The archived panel's filter/sort/term are component-owned, so the
        // service's reset cannot reach them — a scope key from the previous org
        // matches nothing in the new one and would leave the panel empty with the
        // filter that emptied it applied but invisible. Driven off the service's
        // post-reset signal rather than scopeKey$ directly: the service resets on
        // orgContextChanged$ TOO, and a plain org switch emits only that one.
        this._drawer.archivedReset$.pipe(untilDestroyed(this)).subscribe(() => {
            this.archivedSearch = '';
            this.archivedScopeFilter = '';
            this.archivedScopeFilterLabel = 'All';
            this.archivedSort = 'recent';
            this._rebuildArchivedView();
            // Lazy everywhere else, but if the panel is ON SCREEN the service just
            // cleared its rows — without a refetch it shows a false empty state.
            if (this.isOpen && this.activeTab === 'archived') {
                this._drawer.loadArchived();
            }
            this._cdr.markForCheck();
        });

        this._drawer.loadPendingProposals();
        this.loadActivitySummary();
        this._drawer.refreshConversations();
    }

    ngOnDestroy(): void {
        this._releasePreviewsNotIn([]);
        this._syncIntercomLauncher(false);
        this.dictation.destroy();
        this._audioLevels.destroy();
        if (this._attentionTimer) {
            clearTimeout(this._attentionTimer);
        }
        // Service owns stream cleanup — host doesn't tear down chat state on
        // navigation. (Drawer host is mounted at app root so this only fires
        // on full logout / app teardown.)
    }

    ngAfterViewChecked(): void {
        this._syncIntercomLauncher(this.isOpen && this.isExpanded);
        this._reportShownFeedbackAsk();
        const scroller = this.conversationScrollerRef?.nativeElement;
        if (!scroller) {
            return;
        }

        // Empty state (hero) — never auto-pin to the bottom; the greeting
        // and status strip live at the top and must be what the user sees
        // when the drawer opens.
        if (this.isEmptyState) {
            this._lastScrollHeight = scroller.scrollHeight;
            return;
        }

        // 1. If the user just submitted a new question, pin their bubble to
        //    the top of the visible drawer so the streaming reply unfolds
        //    underneath. This wins over "stick to bottom" — the user wants
        //    to see what they asked.
        const lastUserIndex = this._findLastUserMessageIndex();
        if (lastUserIndex !== -1 && lastUserIndex !== this._lastUserMessageIndex) {
            this._lastUserMessageIndex = lastUserIndex;
            const bubble = scroller.querySelector<HTMLElement>(
                `[data-user-msg-index="${lastUserIndex}"]`,
            );
            if (bubble) {
                // Top-align with a small gap from the body's top padding.
                scroller.scrollTop = bubble.offsetTop - scroller.offsetTop - 12;
                this._isPinnedToBottom = false;
                this._lastScrollHeight = scroller.scrollHeight;
                return;
            }
        }

        // 2. Only re-pin to the bottom when CONTENT GREW. CD fires for many
        //    reasons (mouse moves, scroll events, sibling state changes) —
        //    blindly resetting scrollTop on every tick fights the user when
        //    they scroll up after streaming ends. Comparing scrollHeight
        //    tells us "is there new content the user might want to see?".
        const grew = scroller.scrollHeight > this._lastScrollHeight;
        if (grew && this._isPinnedToBottom) {
            scroller.scrollTop = scroller.scrollHeight;
        }
        this._lastScrollHeight = scroller.scrollHeight;
    }

    /**
     * Bound from the template. Tracks whether the user has scrolled away
     * from the bottom so the streaming auto-scroll only kicks in when
     * they're actually watching the tail of the conversation.
     */
    onScroll(): void {
        const scroller = this.conversationScrollerRef?.nativeElement;
        if (!scroller) {
            return;
        }
        const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
        this._isPinnedToBottom = distanceFromBottom <= GableDrawerHostComponent.STICK_THRESHOLD_PX;
    }

    private _findLastUserMessageIndex(): number {
        for (let i = this.messages.length - 1; i >= 0; i--) {
            if (this.messages[i].role === 'user') {
                return i;
            }
        }
        return -1;
    }

    // -------------------------------------------------------------------------
    // Header chrome
    // -------------------------------------------------------------------------

    get firstName(): string {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        if (!jwt?.givenNames) {
            return 'there';
        }
        return jwt.givenNames.trim().split(/\s+/)[0] ?? 'there';
    }

    get orgName(): string {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return jwt?.orgName ?? 'your organization';
    }

    /** Portfolio mode (SD-9208) — the drawer talks to the PMC lane and shows the scope banner. */
    get isPmcScoped(): boolean {
        return this._scope.isPmcScoped;
    }

    /** Drill-down community while PMC-scoped (SD-9209), else null. */
    get drillDownOrg(): GableDrillDownOrg | null {
        return this._scope.drillDownOrg;
    }

    get composerPlaceholder(): string {
        if (this.dictation.listening && !this.composerValue) {
            return 'Listening…';
        }
        const drill = this.drillDownOrg;
        if (drill) {
            return `Ask about ${drill.name}…`;
        }
        return this.isPmcScoped ? 'Ask about your portfolio…' : `Ask about ${this.orgName}…`;
    }

    onCommunitySearchChange(term: string): void {
        this.communitySearch = term ?? '';
        this._communitySearch$.next(this.communitySearch);
    }

    /** First focus fetches the initial 25 communities without requiring a keystroke. */
    onCommunitySearchFocus(): void {
        if (!this.communitySearched && !this.communityLoading) {
            this._communitySearch$.next(this.communitySearch);
            return;
        }
        if (this.communityResults.length > 0) {
            this._scrollCommunityResultsIntoView();
        }
    }

    /** The result list renders below the fold (composer hides it) — reveal it once it exists. */
    private _scrollCommunityResultsIntoView(): void {
        setTimeout(() => {
            this.communityResultsRef?.nativeElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
    }

    onPickCommunity(org: Organization): void {
        this._scope.enterDrillDown({ id: org.id, name: org.name });
    }

    onExitDrillDown(): void {
        this._scope.clearDrillDown();
    }

    private _resetCommunityPicker(): void {
        this.communitySearch = '';
        this.communityResults = [];
        this.communityLoading = false;
        this.communitySearched = false;
    }

    get isEmptyState(): boolean {
        return this.messages.length === 0;
    }

    get hasPendingProposals(): boolean {
        return this.pendingProposals.length > 0;
    }

    get hasStreamingAssistant(): boolean {
        return this.messages.some((m) => m.role === 'assistant' && m.streamState === 'streaming');
    }

    /**
     * True while any sandbox deliverable in the conversation is still
     * generating (SD-7240). Used to hold back the thumbs footer +
     * feedback panel until the document card resolves — telemetry on a
     * turn that's still visibly working looks broken. The messages array
     * reference changes when the sandbox resolves (messages$ emission),
     * so OnPush re-evaluates this without manual nudging.
     */
    get hasPendingSandboxCard(): boolean {
        return this.messages.some((m) => m.sandboxState === 'generating');
    }

    /**
     * Empty-state suggestion list: page-aware starters only (per Catherine
     * 2026-07-11 — no topic chips, no section header). Popular is the quiet
     * fallback so routes without starters aren't blank.
     */
    get currentSuggestions(): GableSuggestion[] {
        if (this.pageSuggestions.length > 0) {
            return this.pageSuggestions;
        }
        // Portfolio scope renders server chips only — the static sets are single-org prompts (SD-9208).
        // Drill-down targets one community, so the static fallback is valid there (SD-9209).
        return this.isPmcScoped && !this.drillDownOrg ? [] : this._suggestionsByAction['popular'] ?? [];
    }

    /**
     * Fetch page-aware starter chips for the current route (best-effort;
     * silent fallback to the static "Popular" set). Called when the drawer
     * opens in its empty state; de-duped per URL so reopening is free.
     */
    private loadWelcomeSuggestions(): void {
        const url = this._pageContext.snapshot()?.url ?? null;
        if (!url || url === this._welcomeLoadedForUrl) {
            return;
        }
        this._welcomeLoadedForUrl = url;
        this._aiPoc
            .welcomeSuggestions()
            .pipe(untilDestroyed(this))
            .subscribe((res) => {
                // Ignore a stale response: if the route changed (or a newer
                // fetch started) while this one was in flight, drop it — else
                // a slow dashboard reply could overwrite the fast violations
                // one after navigation.
                if (this._welcomeLoadedForUrl !== url) {
                    return;
                }
                const starters = (res.groups ?? []).find((g) => g.kind === 'page_starter');
                this.pageSuggestions = (starters?.suggestions ?? []).map((s) => ({
                    label: s.label,
                    prompt: s.prompt,
                }));
                this._cdr.markForCheck();
            });
    }

    onClose(): void {
        this._drawer.close();
    }

    onNewChat(): void {
        this.activeTab = 'chat';
        // A new chat clears the draft; a hot mic would dictate the tail of the
        // old thought into it, and `_preListenValue` would restore the old
        // conversation's text on cancel.
        this._stopDictationForHiddenComposer();
        this._drawer.startNewConversation();
    }

    // -------------------------------------------------------------------------
    // Autopilot preview UI — tabs + hero
    // -------------------------------------------------------------------------

    aiChatbotEnabled = false;

    /** Configure takes the page to the AI chatbot settings, so the dialog steps down to the drawer to let it be seen. */
    onAiSettingsConfigure(): void {
        if (this.isExpanded) {
            this._drawer.setMode('drawer');
        }
    }

    /** AI settings live on the AI chatbot page: show the status here and take the page to it. Mode only changes on the shrink icon or Configure. */
    onOpenAiSettings(): void {
        this.onSelectTab('ai-settings');
        this._websiteBuilder.getWebsite().pipe(untilDestroyed(this)).subscribe({
            next: (site) => {
                this.aiChatbotEnabled = site?.aiChatbot?.enabled === true;
                this._cdr.markForCheck();
            },
            error: (err) => this._growler.fromErrorResponse(err),
        });
        this._router.navigateByUrl('/app/other-tools/ai-chatbot');
    }

    onSelectTab(tab: GableDrawerTab, filter: GableActivityFilter = 'all'): void {
        if (tab !== 'chat') {
            this._stopDictationForHiddenComposer();
        }
        this.activeTab = tab;
        this.activityFilter = filter;
        if (tab === 'prefs') {
            this.loadPreferences();
        }
        if (tab === 'archived') {
            // Lazy: archived is never fetched on drawer open, only when the
            // panel is actually opened ("no 2 GETs on open").
            this._drawer.loadArchived();
        }
        this._cdr.markForCheck();
    }

    /** Compact-mode tab strip order; Workflows and Skills only occupy a slot when visible. */
    private get _compactTabs(): GableDrawerTab[] {
        return (['chat', 'activity', 'prefs', this.canManageWorkflows ? 'workflows' : null, this.canManageWorkflows ? 'skills' : null] as const)
            .filter(Boolean) as GableDrawerTab[];
    }

    get compactTabs(): { value: GableDrawerTab; label: string; icon: string }[] {
        return [{ value: 'chat' as GableDrawerTab, label: 'Chat', icon: 'chat-circle' }, ...this.railSections.map(({ value, label, icon }) => ({ value, label, icon }))]
            .filter((tab) => this._compactTabs.includes(tab.value));
    }

    get compactTabValue(): string {
        return this._compactTabs.includes(this.activeTab) ? this.activeTab : 'chat';
    }

    readonly activeChatMenu = [
        { value: 'rename', label: 'Rename chat', icon: 'pencil-simple' },
        { value: 'archive', label: 'Archive chat', icon: 'archive' },
    ];

    onActiveChatMenu(action: string): void {
        if (action === 'rename') {
            this.onRenameActiveChat();
        } else if (action === 'archive') {
            this.onArchiveActiveChat();
        }
    }

    private _intercomHidden = false;

    /** Intercom's bubble sits above every layer the app has, so it steps aside while the expanded view is open. */
    private _syncIntercomLauncher(hide: boolean): void {
        if (hide !== this._intercomHidden) {
            this._intercomHidden = hide;
            this._intercom.setLauncherHidden(hide);
        }
    }

    onDrawerResize(width: number): void {
        this._drawer.setWidth(width);
    }

    /** The title menu carries only the most recent few, in one bucket. */
    get menuRecents(): { value: string; label: string }[] {
        return this.recentMenuConversations.map((c) => ({ value: String(c.id), label: this.conversationLabel(c) }));
    }

    // -------------------------------------------------------------------------
    // Preferences (SD-9302)
    // -------------------------------------------------------------------------

    /** Refetch on every tab activation (SD-9302) — chat's save_gable_preference tool can add rows mid-session. */
    loadPreferences(): void {
        if (this.preferencesLoading) {
            return;
        }
        // A GET issued alongside an in-flight write has no ordering guarantee:
        // a snapshot taken before the write committed can land last and put the
        // pre-edit body back on screen until the next activation.
        if (this.savingPreference || this.savingPreferenceEdit) {
            return;
        }
        // Activating the tab is a fresh visit: an add box left open from the
        // last one would be the first thing the panel showed, which is the
        // state this change exists to remove.
        this.prefsComposerOpen = false;
        this.newPreferenceText = '';
        this._fetchPreferences();
    }

    retryLoadPreferences(): void {
        this._fetchPreferences();
    }

    private _fetchPreferences(): void {
        // A refetch replaces every row object, so an open editor would be
        // pointing at a row that no longer exists in the list.
        this.editingPreferenceId = null;
        this.preferencesLoadFailed = false;
        this.preferencesLoading = true;
        this._cdr.markForCheck();
        this._preferencesService.list().pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.preferences = res.preferences ?? [];
                this._rebuildPrefsView();
                this.preferencesLoading = false;
                this._cdr.markForCheck();
            },
            error: () => {
                this.preferencesLoadFailed = true;
                this.preferencesLoading = false;
                this._cdr.markForCheck();
            },
        });
    }

    onAddPreference(): void {
        const text = this.newPreferenceText.trim();
        if (!text || this.savingPreference) {
            return;
        }
        this.savingPreference = true;
        this._preferencesService.create(text).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.savingPreference = false;
                this.preferences = res.preferences ?? [];
                // A search or source filter left in place can exclude the row
                // that was just saved, which reads as "it didn't work".
                this.prefsSearch = '';
                this.prefsSourceFilter = '';
                this._rebuildPrefsView();
                // Collapses the box and hands focus back to the trigger — the
                // saved instruction is now a row, and leaving an empty composer
                // open would push the list it just joined down the panel.
                this.onClosePrefsComposer();
                // NOT delegated to onClosePrefsComposer: it early-returns when the
                // composer is already closed (Cancel, Escape or a row pencil during
                // the POST), and this OnPush view would then never paint the row.
                this._cdr.markForCheck();
            },
            error: (err) => {
                this.savingPreference = false;
                this._growler.fromErrorResponse(err);
                this._cdr.markForCheck();
            },
        });
    }

    pendingDeletePreference: GablePreference | null = null;

    onDeletePreference(pref: GablePreference): void {
        this.pendingDeletePreference = pref;
    }

    onDeletePreferenceClosed(confirmed: boolean): void {
        const pref = this.pendingDeletePreference;
        this.pendingDeletePreference = null;
        if (!confirmed || !pref) {
            return;
        }
        this._preferencesService.remove(pref.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    this.preferences = res.preferences ?? this.preferences.filter((p) => p.id !== pref.id);
                    this._rebuildPrefsView();
                    this._cdr.markForCheck();
                },
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    readonly prefRowMenu = [
        { value: 'edit', label: 'Edit', icon: 'pencil-simple' },
        { value: 'delete', label: 'Delete', icon: 'trash', destructive: true },
    ];

    onPrefRowMenu(action: string, pref: GablePreference): void {
        if (action === 'edit') {
            this.onEditPreference(pref);
        } else if (action === 'delete') {
            this.onDeletePreference(pref);
        }
    }

    onEditPreference(pref: GablePreference, event?: Event): void {
        // The other half of the one-editor-at-a-time rule. The composer's DRAFT is
        // kept: closing the box is enough to satisfy the rule, and discarding a
        // half-typed instruction because the user clicked a pencil to check the
        // wording of an existing one is a silent data loss. `loadPreferences`
        // still clears it on tab activation, so it cannot leak across visits.
        this.prefsComposerOpen = false;
        // Focus has to come back to this button when the editor closes; the
        // editor replaces the read row, so the button is gone by then.
        this._prefsEditReturnFocus = (event?.currentTarget as HTMLElement | null) ?? null;
        this.editingPreferenceId = pref.id;
        this.editingPreferenceText = pref.body;
        this._cdr.markForCheck();
        // The textarea is created by the *ngIf this same change detection pass
        // renders, so the focus has to wait for it to exist.
        setTimeout(() => this._focusKitControl(this.prefsEditInputRef?.nativeElement, true));
    }

    /**
     * Focus follows the swap in both directions — the trigger and the composer
     * replace each other through an *ngIf, so the element to focus does not
     * exist until the next change detection pass runs.
     */
    onOpenPrefsComposer(): void {
        if (this.prefsComposerOpen) {
            this.focusPrefsComposer();
            return;
        }
        // One editor at a time; an open row edit is the other one.
        this.onCancelEditPreference();
        this.prefsComposerOpen = true;
        this._cdr.markForCheck();
        setTimeout(() => this.focusPrefsComposer());
    }

    /**
     * Also the Escape and post-save exit — every close hands focus back to
     * whichever add affordance is on screen: the ghost trigger over a list, or
     * the empty state's CTA. Both carry `#prefsAddTrigger` and only one is ever
     * mounted.
     */
    onClosePrefsComposer(): void {
        if (!this.prefsComposerOpen) {
            return;
        }
        this.prefsComposerOpen = false;
        this.newPreferenceText = '';
        this._cdr.markForCheck();
        // Reached from the add response as well as from a click, so the panel it
        // focuses into may be closed or switched away by the time it fires. The
        // drawer closes with a transform, not `display: none`, so the focus would
        // land off screen. Same orphan test as the row editor: the user may have
        // moved into the search box while the POST was in flight.
        setTimeout(() => {
            if (this.isOpen && this.activeTab === 'prefs' && this._focusIsOrphaned()) {
                this._focusKitControl(this.prefsAddTriggerRef?.nativeElement);
            }
        });
    }

    /** Caret at the end so an example filled in from the empty state can be edited, not just saved. */
    private focusPrefsComposer(): void {
        this._focusKitControl(this.prefsAddInputRef?.nativeElement, true);
    }

    /**
     * Kit elements have no `delegatesFocus`, so focus goes to the control inside
     * the shadow root; `caretToEnd` parks the caret after the existing text.
     */
    private _focusKitControl(host: HTMLElement | null | undefined, caretToEnd = false): void {
        const inner = host?.shadowRoot?.querySelector<HTMLElement>('textarea, input, button, a');
        const target = inner ?? host;
        target?.focus();
        if (caretToEnd && (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)) {
            target.setSelectionRange(target.value.length, target.value.length);
        }
    }

    onNewPreferenceChange(text: string): void {
        this.newPreferenceText = text;
    }

    onEditPreferenceTextChange(text: string): void {
        this.editingPreferenceText = text;
    }

    onCancelEditPreference(): void {
        const returnFocus = this._prefsEditReturnFocus;
        this.editingPreferenceId = null;
        this.editingPreferenceText = '';
        this._prefsEditReturnFocus = null;
        this._cdr.markForCheck();
        // Cancel, Escape and a successful save all unmount the textarea that holds
        // focus, which would otherwise drop to <body> behind an untrapped drawer.
        // Safe to focus a hidden button: `.gable-prefs-actions` is opacity-0, and
        // the row's `:focus-within` rule reveals it the instant focus arrives.
        if (returnFocus) {
            setTimeout(() => {
                if (this.isOpen && this.activeTab === 'prefs' && this._focusIsOrphaned()) {
                    this._focusKitControl(returnFocus);
                }
            });
        }
    }

    /** The Edit pencil that opened the row editor, so closing it can hand focus back. */
    private _prefsEditReturnFocus: HTMLElement | null = null;

    /**
     * Whether the unmount actually orphaned focus. Restoring unconditionally
     * STEALS it: the search and source-filter handlers close an open row editor
     * (so a filter can't hide a live textarea), which would otherwise yank focus
     * out of the search box on the first keystroke and send the rest of the word
     * to a button. Removing a focused element leaves `activeElement` at <body>, so
     * that is the only state a restore is owed.
     */
    private _focusIsOrphaned(): boolean {
        const active = document.activeElement;
        return active === null || active === document.body;
    }

    /**
     * No-op when the text is unchanged: a PUT that rewrites a row to what it
     * already says is a wasted round trip, and the server would answer 422 on
     * its own duplicate check for any OTHER row holding that body.
     */
    onSavePreferenceEdit(pref: GablePreference): void {
        const text = this.editingPreferenceText.trim();
        if (!text || this.savingPreferenceEdit) {
            return;
        }
        if (text === pref.body) {
            this.onCancelEditPreference();
            return;
        }
        this.savingPreferenceEdit = true;
        // Captured: nothing disables the other rows' pencils while this PUT is in
        // flight, so the editor open when the response lands may belong to a
        // different row — closing it blind would drop that row's draft.
        const savedId = pref.id;
        this._preferencesService.update(pref.id, text).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.savingPreferenceEdit = false;
                this.preferences = res.preferences ?? this.preferences.map((p) => (p.id === savedId ? { ...p, body: text } : p));
                // Same reason as the add path: a filter left in place can exclude
                // the row that was just saved, which reads as a delete.
                this.prefsSearch = '';
                this.prefsSourceFilter = '';
                this._rebuildPrefsView();
                if (this.editingPreferenceId === savedId) {
                    this.onCancelEditPreference();
                }
                this._cdr.markForCheck();
            },
            error: (err) => {
                this.savingPreferenceEdit = false;
                if (err?.status === 404) {
                    // The row is gone — every further Save would 404 too, so the
                    // editor has to close and the list has to catch up. Branched
                    // explicitly rather than via a fromErrorResponse default,
                    // which would also fire on a status-0 drop and an empty 500.
                    this._growler.oops('This instruction was already deleted.');
                    this.onCancelEditPreference();
                    this._fetchPreferences();
                    return;
                }
                // Stay in edit mode — the 422 the server sends for a duplicate
                // is fixable right here, and dropping the draft would make the
                // user retype it.
                this._growler.fromErrorResponse(err);
                this._cdr.markForCheck();
            },
        });
    }

    /** Enter saves, Shift+Enter breaks the line, Escape abandons — as in the composer. */
    onPrefsEditKeydown(event: KeyboardEvent, pref: GablePreference): void {
        if (event.isComposing) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.onCancelEditPreference();
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSavePreferenceEdit(pref);
        }
    }

    trackByAttachmentFileId(_: number, att: { uploadedFileId: number }): number {
        return att.uploadedFileId;
    }

    /** The kit icon for a sent attachment's chip; the kit has no per-office-format file icons. */
    attachmentKitIcon(fileName: string | null | undefined): string {
        const ext = (fileName || '').toLowerCase().split('.').pop() || '';
        return ext === 'pdf' ? 'file-pdf' : ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext) ? 'image' : 'file-text';
    }

    attachmentIcon(fileName: string | null | undefined): string {
        return attachmentIconClass(fileName);
    }

    trackByPreferenceId(_: number, p: GablePreference): number {
        return p.id;
    }

    /**
     * Client-side, unlike the archive's server-side search: the whole list is
     * already in memory (the endpoint is unpaged) so a round trip per keystroke
     * would buy nothing.
     *
     * Precomputed rather than a getter: the template reads it four times per pass
     * (the count header, `prefsCountLabel`, the `*ngFor` and the no-match block),
     * so a getter re-ran the filter on all four — the same cost `_archivedMeta`
     * exists to avoid on the panel above.
     */
    filteredPreferences: GablePreference[] = [];

    /**
     * Empty only when both sources are present — the same "a one-choice filter
     * is noise" rule the archived scope menu follows. An applied filter keeps
     * the trigger mounted even if it has narrowed the list to one source, or
     * there'd be no control left to clear it.
     */
    get prefsSourceOptions(): { key: GablePreferenceSource; label: string }[] {
        return this._prefsSourceOptions;
    }

    private _prefsSourceOptions: { key: GablePreferenceSource; label: string }[] = [];

    /**
     * Per-row meta labels plus the filtered list and the source menu, all rebuilt
     * once when the rows or the filters change rather than per render.
     * `preferenceMetaLabel` falls through to `LegFiJwtService.getTimezone()` — an
     * uncached localStorage read plus a base64 decode plus a JSON.parse, measured
     * at 26.75 µs — which the template would otherwise pay per row per pass, and
     * the drawer marks itself dirty ~10x/sec while a chat turn streams.
     * Mirrors `_rebuildArchivedView`.
     */
    private readonly _prefsMeta = new Map<number, string>();

    private _rebuildPrefsView(): void {
        this._prefsMeta.clear();
        this.preferences.forEach((p) => this._prefsMeta.set(p.id, this._buildPreferenceMetaLabel(p)));
        this._prefsSourceOptions = this._buildPrefsSourceOptions();

        const term = this.prefsSearch.trim().toLowerCase();
        const source = this.prefsSourceFilter;
        if (term === '' && source === '') {
            this.filteredPreferences = this.preferences;
            return;
        }
        this.filteredPreferences = this.preferences.filter((p) => {
            if (source !== '' && p.source !== source) {
                return false;
            }
            return term === '' || p.body.toLowerCase().includes(term);
        });
    }

    private _buildPrefsSourceOptions(): { key: GablePreferenceSource; label: string }[] {
        const kinds = new Set(this.preferences.map((p) => p.source));
        if (kinds.size < 2 && this.prefsSourceFilter === '') {
            return [];
        }
        return [
            { key: 'chat', label: 'From chat' },
            { key: 'settings', label: 'Added here' },
        ];
    }

    get prefsSourceItems(): { value: string; label: string }[] {
        return [
            { value: FILTER_ALL, label: 'All sources' },
            ...this.prefsSourceOptions.map((o) => ({ value: o.key, label: o.label })),
        ];
    }

    trackByExamplePreference(_index: number, example: string): string {
        return example;
    }

    onPrefsSourceSelect(value: string): void {
        this.onPrefsSourceFilterChange(value === FILTER_ALL ? null : (value as GablePreferenceSource));
    }

    onPrefsSearchChange(term: string): void {
        this.prefsSearch = term;
        // Filtering the edited row out unmounts its textarea with the editor still
        // open, and clearing the filter later resurrects it unfocused and
        // unfocused. Same reasoning as `_fetchPreferences`.
        this.onCancelEditPreference();
        this._rebuildPrefsView();
        this._cdr.markForCheck();
    }

    onClearPrefsSearch(): void {
        this.onPrefsSearchChange('');
        this._focusKitControl(this.prefsSearchInputRef?.nativeElement);
    }

    onPrefsSourceFilterChange(key: GablePreferenceSource | null): void {
        this.prefsSourceFilter = key ?? '';
        this.onCancelEditPreference();
        this._rebuildPrefsView();
        this._cdr.markForCheck();
    }

    /** Opens the composer already filled from an example — one tap, not open-then-type. */
    onUseExamplePreference(text: string): void {
        // One editor at a time; the composer and the list can now coexist, so an
        // open row edit is reachable from here.
        this.onCancelEditPreference();
        this.newPreferenceText = text;
        this.prefsComposerOpen = true;
        this._cdr.markForCheck();
        // The textarea is created by the *ngIf this pass renders.
        setTimeout(() => this.focusPrefsComposer());
    }

    /**
     * Same contract as the row editor and the chat composer — Enter saves,
     * Shift+Enter breaks the line, Escape abandons. One convention for every
     * textarea in the drawer; `isComposing` guards IME candidate selection,
     * which also fires Enter.
     */
    onPrefsKeydown(event: KeyboardEvent): void {
        if (event.isComposing) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.onClosePrefsComposer();
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onAddPreference();
        }
    }

    /**
     * The line under the instruction: where it came from and when. The 'settings'
     * source is left unnamed — it labels the default (added right here) and
     * carries no information the row doesn't already imply.
     */
    preferenceMetaLabel(p: GablePreference): string {
        return this._prefsMeta.get(p.id) ?? '';
    }

    private _buildPreferenceMetaLabel(p: GablePreference): string {
        const date = this._relativeDateLabel(p.createdAt);
        const source = p.source === 'chat' ? 'From chat' : '';
        return [source, date].filter((part) => part !== '').join(' \u00B7 ');
    }

    /** Shown only while a search or source filter is narrowing the list; the header badge holds the total. */
    get prefsCountLabel(): string {
        return `${this.filteredPreferences.length} of ${this.preferences.length} instructions`;
    }

    get prefsFiltering(): boolean {
        return this.prefsSearch.trim() !== '' || this.prefsSourceFilter !== '';
    }

    /** A live filter keeps the controls mounted, or there would be nothing left to clear it with. */
    get showPrefsSearch(): boolean {
        return this.preferences.length > PREFS_SEARCH_MIN_ROWS || this.prefsFiltering;
    }

    /** Shown only as the limit comes into view — a counter on an empty field is noise. */
    get prefsCharsLeft(): number | null {
        return this.charsLeft(this.newPreferenceText);
    }

    /** Same counter for the row editor, which carries the same `maxlength`. */
    get prefsEditCharsLeft(): number | null {
        return this.charsLeft(this.editingPreferenceText);
    }

    /** Shared by the composer and the row editor so the two limits can't drift. */
    charsLeft(text: string): number | null {
        const left = PREFS_MAX_CHARS - text.length;
        return left <= 100 ? left : null;
    }

    /**
     * What the screen reader hears, which is NOT what the counter shows. A polite
     * region bound to the visible count re-announces on every keystroke past 400
     * characters ("100 characters left, 99 characters left, …"), burying whatever
     * the user was actually doing. Milestones only; the visible counter stays
     * continuous.
     */
    prefsCharsLeftAnnouncement(text: string): string {
        const left = PREFS_MAX_CHARS - text.length;
        if (left > 100) {
            return '';
        }
        if (left <= 10 || left % 25 === 0) {
            return `${left} characters left`;
        }
        return '';
    }

    /**
     * Empty-state copy that names the actual cause — blaming "your search" when
     * only the source filter is narrowing sends the user to clear an empty box.
     * Mirrors `archivedEmptyMessage`.
     */
    get prefsEmptyMessage(): string {
        const searching = this.prefsSearch.trim() !== '';
        const filtering = this.prefsSourceFilter !== '';
        if (searching && filtering) {
            return 'No instructions match your search in this source.';
        }
        if (searching) {
            return 'No instructions match your search.';
        }
        if (filtering) {
            return 'No instructions from this source.';
        }
        return 'No instructions yet.';
    }

    /**
     * Hero module quick links — one per module that actually has activity
     * (pending or decided), built from the activity-summary counts. Click
     * opens the Activity tab pre-filtered to that module.
     */
    get moduleLinks(): {
        module: GableActivityFilter;
        label: string;
        description: string;
        tagline: string;
        icon: string;
        tone: string;
        count: number;
    }[] {
        const counts = new Map<string, { pending: number; decided: number }>();
        // Decided counts come from the (cached) activity summary…
        for (const item of this.activitySummary) {
            const module = moduleForActionType(item.actionType);
            if (module === 'other') {
                continue;
            }
            const prior = counts.get(module) ?? { pending: 0, decided: 0 };
            counts.set(module, { pending: prior.pending, decided: prior.decided + item.decided });
        }
        // …but the PENDING count is derived live from pendingProposals$ (grouped
        // via moduleForActionType) so a module card's "N items to review" ticks
        // down the moment an item is approved/dismissed, without a summary refetch.
        for (const proposal of this.pendingProposals) {
            const module = moduleForActionType(proposal.actionType);
            if (module === 'other') {
                continue;
            }
            const prior = counts.get(module) ?? { pending: 0, decided: 0 };
            counts.set(module, { pending: prior.pending + 1, decided: prior.decided });
        }
        // Only modules with items to review render a card — decided-only modules
        // have nothing to act on, and their history lives in Activity.
        return Array.from(counts.entries())
            .filter(([, c]) => c.pending > 0)
            .map(([module, c]) => {
                const meta = GABLE_MODULE_CARDS[module as Exclude<GableActivityFilter, 'all'>];

                return {
                    module: module as GableActivityFilter,
                    label: MODULE_LABELS[module as Exclude<GableActivityFilter, 'all'>],
                    description: `${c.pending} item${c.pending === 1 ? '' : 's'} to review`,
                    tagline: meta.tagline,
                    icon: meta.icon,
                    tone: meta.tone,
                    count: c.pending,
                };
            });
    }

    // Hero status strip — real counts (mock copy retired 2026-07-11).

    get heroStripPending(): number {
        return this.pendingProposals.length;
    }

    private get _totalDecided(): number {
        return this.activitySummary.reduce((sum, item) => sum + item.decided, 0);
    }

    /**
     * Show the "Needs your attention" strip ONLY when something is actually
     * pending. A zero-pending org has nothing to attend to — never render a
     * "View all 0 items" CTA just because items were decided in the past
     * (that history lives in Activity). Per Catherine 2026-07-15.
     */
    get showHeroStrip(): boolean {
        return this.heroStripPending > 0;
    }

    get heroStripTitle(): string {
        if (this.heroStripPending > 0) {
            return `${this.heroStripPending} item${this.heroStripPending === 1 ? '' : 's'} need your review`;
        }
        return `You're all caught up`;
    }

    get heroStripDesc(): string {
        if (this.heroStripPending > 0) {
            return 'Everything that needs you is in the review queue.';
        }
        return `${this._totalDecided} action${this._totalDecided === 1 ? '' : 's'} decided so far — the full history is in Activity.`;
    }

    /**
     * The hero strip is the single "needs you" owner (contract §5): when
     * anything is pending its CTA sends the user straight to the review queue.
     */
    get heroStripCta(): string {
        return this.heroStripPending > 0
            ? `View all ${this.heroStripPending} in the review queue`
            : 'View activity';
    }

    /** Fetch the per-module counts behind the hero quick links (best-effort). */
    private loadActivitySummary(): void {
        // Gable isn't enabled for this org (e.g. after switching to an org
        // without the flag) — skip the fetch so it doesn't 403 (SD-7937). Also
        // skip for anyone who can't see the drawer at all (SD-8321): the summary
        // is pure waste for them.
        //
        // Gated on `canUseGable`, NOT `isSuperUser` (SD-9015): this endpoint is
        // /proposals/activity-summary on GableController, which the beta allowlist
        // now reaches — so a super-only check here would blank the hero strip for
        // exactly the testers we invited.
        // PMC scope bypasses the JWT-org flag — core checks the PMC's flags (SD-9074 wave 2).
        if ((!this._drawer.flagEnabled && !this._scope.isPmcScoped) || !this._drawer.canUseGable) {
            return;
        }
        this._aiPoc.getActivitySummary()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    this.activitySummary = res.items ?? [];
                    this._cdr.markForCheck();
                },
                error: () => undefined,
            });
    }

    trackByModuleLink(_: number, link: { module: string }): string {
        return link.module;
    }

    trackBySuggestionCard(index: number, card: GableSuggestionCardPayload): string {
        return `${index}:${card.title}`;
    }

    /** Suggestion-card item button → follow-up chat prompt asking the box to
     *  propose the action (which comes back as a confirmable preview card). */
    onSuggestionActionPrompt(prompt: string): void {
        if (this.isSending) {
            return;
        }
        this._drawer.sendMessage(prompt);
    }

    /** Time-of-day greeting for the empty-state hero. */
    get heroGreeting(): string {
        // Org timezone, not browser — matches the app-wide timezone rule.
        const tz = LegFiJwtService.getTimezone() || moment.tz.guess();
        const hour = moment.tz(tz).hour();
        const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        return `Good ${part}, ${this.firstName}`;
    }

    // -------------------------------------------------------------------------
    // Chat history (SD-6742)
    // -------------------------------------------------------------------------

    /**
     * Header title for the chat view: the loaded conversation's title when one
     * is set, "New AI chat" on an empty fresh chat, otherwise "Gable AI".
     */
    /**
     * Blurb for the active panel, shown on the info icon beside the header
     * title. The panels used to carry this as a permanent subtitle under a
     * badge, which cost ~90px of every screen that had content to show; it now
     * lives on hover here and as the empty state's own copy.
     *
     * Null on chat, which describes itself.
     */
    get activeTabInfo(): string | null {
        // Each one answers the same two questions: what is on this page, and
        // what do you do here. Instructions and Skills both sound like "tell
        // Gable what to do", so each says explicitly what the other is not.
        if (this.activeTab === 'activity') {
            return 'Every action Gable has proposed or taken. Approve or dismiss what is waiting on you, '
                + 'and see what already went through.';
        }
        if (this.activeTab === 'prefs') {
            return 'Your own standing rules — Gable follows them in every conversation, without being asked. '
                + 'They apply to your chats only, not your teammates\'.';
        }
        if (this.activeTab === 'skills') {
            return 'Reference material Gable pulls in when the work matches it — house style, a policy, '
                + 'a checklist. Skills shape how Gable writes and decides, never what it may do without '
                + 'your approval.';
        }
        if (this.activeTab === 'workflows') {
            return 'Things you have asked Gable to do on a schedule. Each run writes a summary and, if you chose it, '
                + 'stages suggestions in Activity or emails the people you picked.';
        }
        if (this.activeTab === 'ai-settings') {
            return 'How the AI chatbot on your public website behaves, and what it knows about your community.';
        }
        if (this.activeTab === 'archived') {
            return 'Chats you have moved out of recents. Open one to read it, or restore it to put it back '
                + 'in the sidebar.';
        }
        return null;
    }

    get chatHeaderTitle(): string {
        // The title names the active VIEW, in both modes: expanded reads it as
        // the pane heading beside the rail, and the drawer reads it as "where am
        // I" beside the caret that navigates. It used to say 'Gable AI' in the
        // drawer, which named the product rather than the place.
        if (this.activeTab === 'activity') {
            return 'Activity';
        }
        if (this.activeTab === 'prefs') {
            return 'Instructions';
        }
        if (this.activeTab === 'skills') {
            return 'Skills';
        }
        if (this.activeTab === 'workflows') {
            return 'Workflows';
        }
        if (this.activeTab === 'archived') {
            return 'Archived chats';
        }
        if (this.activeTab === 'ai-settings') {
            return 'AI settings';
        }
        // Chat: the conversation's own name, and a fresh one matches the label
        // on the New chat row it is selected from.
        if (this.activeTitle && this.activeTitle.trim() !== '') {
            return this.activeTitle.trim();
        }
        return 'New chat';
    }

    /**
     * "New chat" is the selected rail row whenever the chat tab is showing a
     * conversation that hasn't been persisted yet — i.e. no conversation id.
     * Selecting any recent chat, or any other tab, takes the highlight away.
     */
    get isNewChatSelected(): boolean {
        return this.activeTab === 'chat' && this.activeConversationId === null;
    }

    /** Id of the conversation currently loaded — drives the sidebar highlight. */
    get activeConversationId(): number | null {
        return this._drawer.conversationId;
    }

    /** Drawer-mode history menu shows only the most-recent N chats (kept basic). */
    get recentMenuConversations(): GableConversationSummary[] {
        return this.activeConversations.slice(0, GableDrawerHostComponent.DRAWER_MENU_RECENT_LIMIT);
    }

    /** Display label for one history row — title, else first-user-message hint. */
    conversationLabel(c: GableConversationSummary): string {
        const title = (c.title ?? '').trim();
        return title !== '' ? title : 'New chat';
    }

    /** Tenant label for a chat from another scope; empty when it belongs to the current one. */
    conversationScopeLabel(c: GableConversationSummary): string {
        const scope = c.scope;
        if (!scope?.id) {
            return '';
        }
        const here = this._scope.isPmcScoped && !this.drillDownOrg
            ? scope.kind === 'pmc' && scope.id === this._scope.pmcId
            : scope.kind === 'org' && scope.id === (this.drillDownOrg?.id ?? LegFiJwtService.read()?.orgId);
        if (here) {
            return '';
        }
        const name = (scope.name ?? '').trim();
        return scope.kind === 'pmc' ? `${name || 'Portfolio'} · portfolio` : name;
    }

    /**
     * Tenant label for the OPEN chat — the header's one-line stand-in for the
     * per-row scope labels the sidebar used to carry. Empty (so the line does
     * not render) when the chat belongs to the scope the user is already in,
     * which `conversationScopeLabel` decides.
     *
     * Memoized on the conversation id plus both row-array identities: this is a
     * CD-bound getter, and `conversationScopeLabel` falls through to
     * `LegFiJwtService.read()` — a localStorage read plus a base64 decode plus a
     * JSON.parse — which would otherwise be paid on every pass.
     */
    get headerScopeLabel(): string {
        if (this.activeTab !== 'chat') {
            return '';
        }
        const id = this.activeConversationId;
        if (id === null) {
            return '';
        }
        const active = this.activeConversations;
        const archived = this.archivedConversations;
        const cached = this._headerScopeCache;
        if (cached && cached.id === id && cached.active === active && cached.archived === archived) {
            return cached.label;
        }
        // Archived is searched too: an archived chat can be opened straight from
        // the Archived panel, and it keeps its scope while it sits there.
        const conversation = active.find((c) => c.id === id)
            ?? archived.find((c) => c.id === id);
        const label = conversation ? this.conversationScopeLabel(conversation) : '';
        this._headerScopeCache = { id, active, archived, label };
        return label;
    }

    private _headerScopeCache: {
        id: number;
        active: GableConversationSummary[];
        archived: GableConversationSummary[];
        label: string;
    } | null = null;

    /** True when a search term is active — drives the empty-state copy branch. */
    get isSearchingHistory(): boolean {
        return this.historySearch.trim() !== '';
    }

    readonly railFooter = [{ value: 'archived', label: 'Archived chats', icon: 'archive' }];
    readonly railChatMenu = [
        { value: 'rename', label: 'Rename', icon: 'pencil-simple' },
        { value: 'archive', label: 'Archive', icon: 'archive' },
    ];

    /** The rail's section links, in the order and with the gates the old markup had. */
    get railSections(): { value: GableDrawerTab; label: string; icon: string; count?: number }[] {
        return [
            ...(this.autopilotUi ? [{ value: 'activity' as GableDrawerTab, label: 'Activity', icon: 'clock-counter-clockwise', count: this.pendingProposals.length || undefined }] : []),
            { value: 'prefs' as GableDrawerTab, label: 'Instructions', icon: 'article' },
            ...(this.canManageWorkflows ? [{ value: 'workflows' as GableDrawerTab, label: 'Workflows', icon: 'lightning-a' }] : []),
            ...(this.canManageWorkflows ? [{ value: 'skills' as GableDrawerTab, label: 'Skills', icon: 'scroll' }] : []),
            { value: 'ai-settings' as GableDrawerTab, label: 'AI settings', icon: 'gear' },
        ];
    }

    /** Recent chats, bucketed the way the rail labels them. */
    get railRecents(): { value: string; label: string; group: string }[] {
        const row = (group: string) => (c: GableConversationSummary) => ({ value: String(c.id), label: this.conversationLabel(c), group });
        return [
            ...this.recentConversations.map(row('Recents')),
            ...this.yesterdayConversations.map(row('Yesterday')),
            ...this.olderConversations.map(row('Older')),
        ];
    }

    /** The rail's open row: `new`, a tab, or the open chat's id. */
    get railValue(): string {
        if (this.isNewChatSelected) {
            return 'new';
        }
        if (this.activeTab === 'chat') {
            return this.activeConversationId !== null ? String(this.activeConversationId) : '';
        }
        return this.activeTab;
    }

    onRailChange(value: string): void {
        if (value === 'new') {
            this.onNewChat();
            return;
        }
        if (value === 'ai-settings') {
            this.onOpenAiSettings();
            return;
        }
        if (['activity', 'prefs', 'workflows', 'skills', 'archived'].includes(value)) {
            this.onSelectTab(value as GableDrawerTab);
            return;
        }
        const chat = this.activeConversations.find((c) => String(c.id) === value);
        if (chat) {
            this.onSelectConversation(chat);
        }
    }

    get editingConversation(): GableConversationSummary | null {
        return this.editingConversationId === null ? null : this.activeConversations.find((c) => c.id === this.editingConversationId) ?? null;
    }

    onRailChatMenu(action: string, c: GableConversationSummary): void {
        if (action === 'rename') {
            this.onRenameConversation(c);
        } else if (action === 'archive') {
            this.onArchiveConversation(c);
        }
    }

    onRailSearchInput(event: Event): void {
        this.onHistorySearchChange((event.composedPath()[0] as HTMLInputElement).value ?? '');
    }

    onRailRenameInput(event: Event): void {
        this.editingTitle = (event.composedPath()[0] as HTMLInputElement).value ?? '';
    }

    /** Sidebar chat-history time buckets — last 24h. */
    get recentConversations(): GableConversationSummary[] {
        return this.activeConversations.filter((c) => this._historyBucket(c) === 'recent');
    }

    /** Sidebar chat-history time buckets — 24-48h ago. */
    get yesterdayConversations(): GableConversationSummary[] {
        return this.activeConversations.filter((c) => this._historyBucket(c) === 'yesterday');
    }

    /** Sidebar chat-history time buckets — everything older than 48h. */
    get olderConversations(): GableConversationSummary[] {
        return this.activeConversations.filter((c) => this._historyBucket(c) === 'older');
    }

    private _historyBucket(c: GableConversationSummary): 'recent' | 'yesterday' | 'older' {
        // A never-messaged chat has no date: the diff is NaN, both tests below are
        // false, and it files under "Older" — which is where a chat with nothing
        // in it belongs.
        const hoursAgo = moment().diff(moment(c.lastMessageAt), 'hours', true);
        if (hoursAgo < 24) { return 'recent'; }
        if (hoursAgo < 48) { return 'yesterday'; }
        return 'older';
    }

    /** Pull fresh history when the drawer-mode title menu opens. */
    onHistoryMenuOpened(): void {
        this._drawer.refreshConversations();
    }

    /**
     * Sidebar search input handler. Server-side now (SD-6742): the term is
     * pushed into the service's debounced (~300ms) switchMap pipeline, which
     * resets to page 1 and matches title OR message content. No client-side
     * filtering.
     */
    /**
     * The rail search filters the chat list AND marks every occurrence in the
     * open thread. These drive the Chrome-style find controls over the thread
     * marks — the filtered list already shows every matching chat, so cycling
     * rows would add nothing; cycling hits inside a chat does.
     */
    onMatchesChanged(state: GableMatchState): void {
        this.matchCount = state.count;
        this.matchActiveIndex = state.activeIndex;
        this._cdr.markForCheck();
    }

    onFindNext(): void {
        this.messageHighlight?.next();
    }

    onFindPrev(): void {
        this.messageHighlight?.prev();
    }

    /** Enter = next, Shift+Enter = previous, Escape = clear (focus stays put). */
    onHistorySearchKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (event.shiftKey) {
                this.onFindPrev();
            } else {
                this.onFindNext();
            }
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.onClearHistorySearch();
        }
    }

    onClearHistorySearch(): void {
        this.onHistorySearchChange('');
    }

    /** Find controls only make sense with a thread on screen to search. */
    get showFindControls(): boolean {
        return this.historySearch.trim() !== ''
            && this.activeTab === 'chat'
            // The thread — and with it the highlight directive — is unmounted
            // while a conversation loads, so without this the controls stay
            // enabled with the PREVIOUS chat's count and do nothing when clicked.
            && !this.historyLoading
            && !this.isEmptyState;
    }

    /**
     * Re-apply find-in-conversation's marks when the thread's markup changed
     * under them: a bubble binds `[innerHTML]="… | markdown: (streaming ?
     * 'streaming' : 'final')"`, so when a turn completes the pipe argument flips
     * and Angular rewrites that bubble wholesale — detaching every <mark> inside
     * it. The counter then reports matches the document no longer owns and
     * next/prev scroll to nothing, and it does not self-heal.
     *
     * Deliberately NOT on every streamed delta. A full re-apply is a TreeWalker
     * plus an unwrap/rewrap of every mark — measured at 13.2 ms on a 60-message
     * thread with a term that matches everywhere — and at ~10 emissions/second
     * that is the per-pass cost this audit just finished removing elsewhere.
     * Completion is the edge that actually invalidates the marks, and the
     * emission carrying it is already an idle one, so skipping while a turn
     * streams costs nothing.
     */
    private _refreshHighlightAfterMessages(): void {
        if (this.hasStreamingAssistant) {
            return;
        }
        // The emission that flips the last bubble to 'complete' is itself an idle
        // one, so the settle is covered without tracking the edge.
        // No-ops unless a term is active; re-applies without scrolling or flashing.
        this.messageHighlight?.refresh();
    }

    onHistorySearchChange(term: string): void {
        this.historySearch = term;
        this._drawer.searchConversations(term);
        this._cdr.markForCheck();
    }

    /**
     * Infinite-scroll for the recent-chats rail. When the list scrolls within
     * a threshold of the bottom AND the server says more pages exist, fetch the
     * next page (appended in the service). The service guards against
     * concurrent / no-more fetches, so spamming this is safe.
     */
    onRailEndReached(): void {
        if (!this.hasMoreConversations || this.conversationsLoading) {
            return;
        }
        this._drawer.loadMoreConversations();
    }

    onSelectConversation(c: GableConversationSummary): void {
        this.activeTab = 'chat';
        this._stopDictationForHiddenComposer();
        this._drawer.loadConversation(c.id, c.scope);
    }

    // -------------------------------------------------------------------------
    // Archived chats panel (expanded mode)
    // -------------------------------------------------------------------------

    /**
     * The archived rows as the panel renders them. Search, scope filter and sort
     * all run server-side, so this is the response order — the panel adds no
     * ordering or filtering of its own. It stays a FIELD rather than a getter:
     * the template reads it three times per change-detection pass and `(scroll)`
     * marks the view dirty on every scroll event, so anything computed here ran
     * at scroll frequency (measured at 13.5 ms/pass over 500 rows).
     */
    archivedRows: GableConversationSummary[] = [];

    /**
     * Per-row labels, built once when a page lands rather than per render.
     * `conversationScopeLabel` falls through to `LegFiJwtService.read()` — a
     * localStorage read plus a base64 decode plus a JSON.parse, measured at
     * 26.75 µs — which the template would otherwise pay once per row per pass.
     */
    private readonly _archivedMeta = new Map<number, { date: string; scope: string }>();

    private _rebuildArchivedView(): void {
        this._archivedMeta.clear();
        this.archivedConversations.forEach((c) => {
            this._archivedMeta.set(c.id, {
                date: this._buildArchivedDateLabel(c),
                scope: this.conversationScopeLabel(c),
            });
        });
        this.archivedRows = this.archivedConversations;
        this._archivedScopeOptions = this._buildArchivedScopeOptions();
    }

    /** Scope label for an archived row, resolved once per page rather than per pass. */
    archivedScopeLabel(c: GableConversationSummary): string {
        return this._archivedMeta.get(c.id)?.scope ?? '';
    }

    /**
     * Scope options for the filter menu, built from the loaded rows. Returned
     * empty when every archived chat lives in one scope — a filter with a single
     * choice is noise.
     */
    get archivedScopeOptions(): { key: string; label: string }[] {
        return this._archivedScopeOptions;
    }

    private _archivedScopeOptions: { key: string; label: string }[] = [];

    /** The tenants the server says this user's archive spans. */
    private _archivedScopes: GableConversationScope[] = [];

    private _buildArchivedScopeOptions(): { key: string; label: string }[] {
        // Server-owned: the filter runs in SQL now, so a filtered response holds
        // exactly one scope and can no longer describe the menu it came from.
        const fromServer = this._archivedScopes.map((scope) => ({
            key: `${scope.kind}:${scope.id}`,
            label: this._scopeOptionLabel(scope),
        }));
        // Belt and braces for the same trap the derived version fell into: if the
        // applied scope is missing from the server's list, keep it as an option
        // anyway — otherwise the trigger unmounts while its filter is still
        // excluding every row, and there is no control left to clear it.
        const applied = this.archivedScopeFilter;
        const options = applied !== '' && !fromServer.some((o) => o.key === applied)
            ? [...fromServer, { key: applied, label: this.archivedScopeFilterLabel }]
            : fromServer;
        // A one-choice filter is noise — unless one is applied, in which case the
        // user needs the trigger to get back to "All".
        return options.length > 1 || applied !== '' ? options : [];
    }

    /** Menu label for a tenant. The current one has no name worth repeating. */
    private _scopeOptionLabel(scope: GableConversationScope): string {
        const name = (scope.name ?? '').trim();
        if (name === '') {
            return scope.kind === 'pmc' ? 'Portfolio' : 'This organization';
        }
        return scope.kind === 'pmc' ? `${name} · portfolio` : name;
    }

    /**
     * Label for the filter trigger — "All" until a scope is picked. Captured from
     * the option the user chose rather than looked up in the current option list:
     * that list is rebuilt from the server's response, and a filtered response is
     * exactly the one that might not describe the filter it applied.
     */
    archivedScopeFilterLabel = 'All';

    /** Right-hand date column. Read-only lookup — built once per page, not per pass. */
    archivedDateLabel(c: GableConversationSummary): string {
        return this._archivedMeta.get(c.id)?.date ?? '';
    }

    /** Relative inside a week, absolute beyond it — in the ORG's timezone. */
    private _buildArchivedDateLabel(c: GableConversationSummary): string {
        // Browser-local would disagree with the Activity panel in this same
        // drawer (gable-activity-panel formats in the org timezone) about which
        // day a late-evening chat belongs to. `legfi-date.pipe` — 577 uses — sets
        // the house rule: dates are shown in the org's timezone.
        // The server contract is `string|null` even though the model types it
        // non-nullable, and `moment(undefined)` is NOW rather than invalid —
        // a never-messaged chat would date itself to this second.
        return this._relativeDateLabel(c.lastMessageAt);
    }

    /**
     * Relative inside a week, absolute beyond it. Shared by the archived rows
     * and the Instructions rows so the drawer's two date slots can't drift.
     * Empty string for anything unusable — the callers render it raw.
     */
    private _relativeDateLabel(iso: string | null): string {
        if (!iso) {
            return '';
        }
        const when = moment.tz(iso, LegFiJwtService.getTimezone() || moment.tz.guess());
        if (!when.isValid()) {
            return '';
        }
        // A server clock ahead of the browser's gives a negative diff, which is
        // `< 7`, so `fromNow()` would render "in 4 minutes" on an archived chat.
        // Same guard as RelativeTimePipe.
        if (when.isAfter(moment())) {
            return 'just now';
        }
        // The year matters here in a way it doesn't elsewhere: core auto-archives
        // at 30 days, so a multi-year archive is the normal population and bare
        // 'MMM D' makes Aug 2025 and Aug 2026 identical.
        return moment().diff(when, 'days') < 7 ? when.fromNow() : when.format('MMM D, YYYY');
    }

    onArchivedSearchChange(term: string): void {
        this.archivedSearch = term;
        this._drawer.searchArchived(term);
        this._cdr.markForCheck();
    }

    onClearArchivedSearch(): void {
        this.onArchivedSearchChange('');
        this._focusKitControl(this.archivedSearchInputRef?.nativeElement);
    }

    readonly archivedSortItems: { value: GableArchivedSort; label: string }[] = [
        { value: 'recent', label: 'Recent activity' },
        { value: 'oldest', label: 'Oldest first' },
        { value: 'title', label: 'Name (A–Z)' },
    ];

    readonly archivedRowMenu = [
        { value: 'restore', label: 'Restore', icon: 'arrow-counter-clockwise' },
        { value: 'rename', label: 'Rename', icon: 'pencil-simple' },
    ];

    get archivedScopeItems(): { value: string; label: string }[] {
        return [
            { value: FILTER_ALL, label: 'All scopes' },
            ...this.archivedScopeOptions.map((o) => ({ value: o.key, label: o.label })),
        ];
    }

    onArchivedScopeSelect(value: string): void {
        this.onArchivedScopeFilterChange(this.archivedScopeOptions.find((o) => o.key === value) ?? null);
    }

    onArchivedRowMenu(action: string, c: GableConversationSummary): void {
        if (action === 'restore') {
            this.onUnarchiveConversation(c);
        } else if (action === 'rename') {
            this.onRenameConversation(c);
        }
    }

    /**
     * Re-order the archived list. The service refetches page 1: the endpoint is
     * paged, so ordering the pages already fetched would order a window rather
     * than the archive — "Oldest first" could never reach the oldest chat.
     */
    onArchivedSortChange(sort: GableArchivedSort): void {
        this.archivedSort = sort;
        this._drawer.setArchivedSort(sort);
        this._cdr.markForCheck();
    }

    /**
     * Narrow the archived list to one tenant, or `null` for all of them. Also a
     * refetch: filtering client-side could leave the panel too short to scroll,
     * and scrolling is what fetches the next page — so a filter matching few of
     * the loaded rows made the rest of its matches unreachable.
     */
    onArchivedScopeFilterChange(opt: { key: string; label: string } | null): void {
        this.archivedScopeFilter = opt?.key ?? '';
        this.archivedScopeFilterLabel = opt?.label ?? 'All';
        this._drawer.setArchivedScopeFilter(this._parseScopeKey(this.archivedScopeFilter));
        this._cdr.markForCheck();
    }

    /** `'org:42'` -> `{ kind: 'org', id: 42 }`; `''` (All) -> `null`. */
    private _parseScopeKey(key: string): GableConversationScopeRef | null {
        const [kind, rawId] = key.split(':');
        const id = Number(rawId);
        if ((kind !== 'org' && kind !== 'pmc') || !Number.isFinite(id) || id <= 0) {
            return null;
        }
        return { kind, id };
    }

    /**
     * True only when the archive is genuinely empty — not when a search or the
     * scope filter has narrowed it to nothing. A filtered-to-zero list still has
     * content behind it, so it keeps the compact header and leans on
     * `archivedEmptyMessage` instead of re-showing the splash.
     *
     * Gated on the load so the splash can't flash before page 1 lands.
     */
    get archivedPanelEmpty(): boolean {
        return !this.archivedLoading
            && !this.archivedLoadFailed
            && !this.isFilteringArchived
            && this.archivedConversations.length === 0;
    }

    /**
     * Latched from `archivedError$`: a page-1 failure clears the rows service-side,
     * so without this a 5xx renders the splash plus "No archived chats yet", which
     * tells the user the opposite of what happened.
     */
    archivedLoadFailed = false;

    /** Same contract as `archivedPanelEmpty`, for the Instructions panel. */
    get prefsPanelEmpty(): boolean {
        return !this.preferencesLoading
            && !this.preferencesLoadFailed
            && this.preferences.length === 0;
    }

    /**
     * Empty-state copy that names the actual cause. Blaming "your search" when
     * only the scope filter is narrowing sends the user to clear an empty box.
     */
    get archivedEmptyMessage(): string {
        const searching = this.archivedSearch.trim() !== '';
        const filtering = this.archivedScopeFilter !== '';
        if (searching && filtering) {
            return 'No archived chats match your search in this organization.';
        }
        if (searching) {
            return 'No archived chats match your search.';
        }
        if (filtering) {
            return 'No archived chats in this organization.';
        }
        return 'No archived chats yet. Archive a chat from its ⋯ menu in the sidebar.';
    }

    /** True when search or a scope filter is narrowing the list (empty-state copy). */
    get isFilteringArchived(): boolean {
        return this.archivedSearch.trim() !== '' || this.archivedScopeFilter !== '';
    }

    /**
     * Infinite scroll for the archived panel. Driven by the virtual viewport
     * rather than a `(scroll)` binding on the panel: the viewport owns the
     * scrolling now, and `scrolledIndexChange` fires far less often than a raw
     * scroll event — which used to mark the view dirty on every frame.
     */
    onArchivedScrolled(): void {
        const viewport = this.archivedViewport;
        if (!viewport || !this.hasMoreArchived || this.archivedLoading) {
            return;
        }
        if (viewport.measureScrollOffset('bottom') <= GableDrawerHostComponent.INFINITE_SCROLL_THRESHOLD_PX) {
            this._drawer.loadMoreArchived();
        }
    }

    /**
     * Scrolling is the ONLY thing that pages this list, so a page that doesn't
     * fill the viewport strands every page after it — no overflow, no scroll, no
     * fetch. Bounded, unlike a filter-driven fetch loop: each page adds rows and
     * the loop ends as soon as the content is taller than the viewport.
     */
    private _fillArchivedViewport(): void {
        const viewport = this.archivedViewport;
        if (!viewport || !this.hasMoreArchived || this.archivedLoading) {
            return;
        }
        const height = viewport.getViewportSize();
        if (height > 0 && viewport.measureRenderedContentSize() <= height) {
            this._drawer.loadMoreArchived();
        }
    }


    /**
     * Rename happens INLINE in the expanded sidebar (no modal — the expanded
     * drawer is itself a dialog, so a modal-on-modal reads badly). Clicking
     * Rename swaps the row's title for an input; commit on Enter/blur, cancel
     * on Escape. (Drawer mode has no rename affordance at all.)
     */
    onRenameConversation(c: GableConversationSummary): void {
        this.editingConversationId = c.id;
        this.editingTitle = this.conversationLabel(c);
        this._cdr.markForCheck();
    }

    /** Commit an inline rename. Guarded so a post-Escape blur is a no-op. */
    commitInlineRename(c: GableConversationSummary): void {
        if (this.editingConversationId !== c.id) {
            return;
        }
        const newTitle = this.editingTitle.trim();
        const current = this.conversationLabel(c);
        this.editingConversationId = null;
        this._cdr.markForCheck();

        if (newTitle === '' || newTitle === current) {
            return; // nothing to do
        }
        this._drawer
            .renameConversation(c.id, newTitle)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat renamed', 'The chat name was updated.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    cancelInlineRename(): void {
        this.editingConversationId = null;
        this._cdr.markForCheck();
    }

    /**
     * Archive with no confirmation — just do it and toast it. We do NOT
     * auto-open the Archived section or highlight the row: that read as a false
     * "selected" state. The user opens Archived themselves to find it.
     */
    onArchiveConversation(c: GableConversationSummary): void {
        this._drawer
            .archiveConversation(c.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat archived', 'Moved to archived chats.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    /**
     * Restore moves the chat back to recents and leaves the user in the Archived
     * panel. It deliberately does NOT open the chat: restore is now a list
     * operation on a full panel, so triaging several in a row shouldn't yank the
     * user into the last one. (It DID open the chat when the only restore
     * affordance was a single rail row.) The row leaving the list plus the toast
     * are the confirmation; the chat is one click away in the rail.
     */
    onUnarchiveConversation(c: GableConversationSummary): void {
        this._drawer
            .unarchiveConversation(c.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._growler.success('Chat restored', 'Moved back to recent chats.');
                    // The restored row leaves the list, destroying the kebab that
                    // the dropdown just returned focus to — which drops focus to <body>
                    // and restarts a keyboard user at the top of the page on every
                    // chat they triage. Park focus somewhere that survives.
                    this._focusKitControl(this.archivedSearchInputRef?.nativeElement);
                },
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    // -------------------------------------------------------------------------
    // Current-chat actions (header overflow menu)
    // -------------------------------------------------------------------------

    /**
     * Rename/archive for the chat that is OPEN, from the header — the only
     * surface present in every mode. Before this, both actions lived exclusively
     * in the expanded rail's row kebab, so a viewport under
     * `EXPANDED_MIN_VIEWPORT` could not rename or archive a chat at all. It also
     * removes a smaller absurdity in expanded mode: renaming the chat you are
     * reading meant hunting for its row in the rail.
     */
    get canManageActiveChat(): boolean {
        return this.activeConversationId !== null
            && this.activeTab === 'chat';
    }

    /**
     * Edit the title in place in the header rather than in a dialog: the title
     * is already on screen there, so the rename happens where the user is
     * looking and needs no new surface.
     */
    onRenameActiveChat(): void {
        if (this.activeConversationId === null) {
            return;
        }
        this.headerTitleDraft = (this.activeTitle ?? '').trim() || this.chatHeaderTitle;
        this.editingHeaderTitle = true;
        this._cdr.markForCheck();
    }

    commitHeaderRename(): void {
        if (!this.editingHeaderTitle) {
            return;
        }
        const id = this.activeConversationId;
        const newTitle = this.headerTitleDraft.trim();
        const current = (this.activeTitle ?? '').trim();
        this.editingHeaderTitle = false;
        this._cdr.markForCheck();

        if (id === null || newTitle === '' || newTitle === current) {
            return;
        }
        this._drawer
            .renameConversation(id, newTitle)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat renamed', 'The chat name was updated.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    cancelHeaderRename(): void {
        this.editingHeaderTitle = false;
        this._cdr.markForCheck();
    }

    /**
     * Archives the open chat and leaves the user in it — same philosophy as
     * `onUnarchiveConversation`: the toast is the confirmation, and yanking the
     * view away would also discard an unsent draft.
     */
    onArchiveActiveChat(): void {
        const id = this.activeConversationId;
        if (id === null) {
            return;
        }
        this._drawer
            .archiveConversation(id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => this._growler.success('Chat archived', 'Moved to archived chats.'),
                error: (err) => this._growler.fromErrorResponse(err),
            });
    }

    trackByConversationId(_: number, c: GableConversationSummary): number {
        return c.id;
    }

    /**
     * Toggle between drawer (right-edge slide) and expanded (full-width
     * centered dialog) modes. Persisted via UserPreferencesService so the
     * choice sticks across sessions.
     */
    onToggleDock(): void {
        this._drawer.toggleMode();
    }

    /**
     * Watch the expanded-layout breakpoint. `matchMedia` over a resize handler:
     * it fires only when the threshold is actually crossed, not on every pixel.
     */
    private _watchViewportWidth(): void {
        const query = window.matchMedia(EXPANDED_MIN_VIEWPORT);
        this._isNarrowViewport = query.matches;
        // untilDestroyed, not a bare addEventListener: the host is mounted under
        // `*ngIf="isLoggedIn"`, so a logout/login cycle without a page reload
        // would otherwise retain a whole drawer tree per cycle.
        fromEvent<MediaQueryListEvent>(query, 'change')
            .pipe(untilDestroyed(this))
            .subscribe((event) => {
                this._isNarrowViewport = event.matches;
                // The mode$ subscription can't cover this — the SERVICE mode doesn't
                // change when the window does, only the effective one does.
                this._syncEffectiveMode();
                this._cdr.markForCheck();
            });
    }

    /**
     * Keep view state legal for the effective mode.
     *
     * Archived used to be evicted here because the docked TAB STRIP had no slot
     * for it — but that strip is gone, and the archived panel is an ordinary
     * full-width body section like Activity/Preferences/Skills, all of which
     * render fine in the drawer. Keeping the eviction was what left a narrow
     * viewport with no way to restore or rename an archived chat at all.
     * Nothing is mode-illegal today; the hook stays as the single place to put
     * such a rule if one appears.
     */
    private _syncEffectiveMode(): void {
        // No mode-illegal tabs at present.
    }

    /**
     * EFFECTIVE mode, not the stored preference. A narrow viewport renders the
     * drawer layout even when the user's saved mode is `expanded` — the stored
     * preference is never rewritten, so widening the window restores the dialog
     * on its own.
     */
    get isExpanded(): boolean {
        return this.mode === 'expanded' && !this.isNarrowViewport;
    }

    /** True while the viewport is too narrow for the expanded dialog's layout. */
    get isNarrowViewport(): boolean {
        return this._isNarrowViewport;
    }

    // -------------------------------------------------------------------------
    // Composer
    // -------------------------------------------------------------------------

    /**
     * What the textarea shows: the committed draft plus the in-flight speech
     * fragment. Binding `draftMessage` directly would hide everything spoken
     * since the last pause until the recognizer finalized it.
     */
    get composerValue(): string {
        return this.dictation.displayValue;
    }

    onDraftChange(text: string): void {
        // Through the controller, not straight to the service: typing during
        // dictation stops the mic and adopts the visible text wholesale.
        this.dictation.onInputChange(text);
    }

    /** Re-check the host only when something outside the composer reads the draft; the composer itself is written directly. */
    private _applyDraft(draft: string): void {
        const hadText = this.draftMessage.trim() !== '';
        const meta = this.composerMeta;
        this.draftMessage = draft;
        this._syncComposerValue();
        if (hadText !== (draft.trim() !== '') || meta !== this.composerMeta) {
            this._cdr.markForCheck();
        }
    }

    private _syncComposerValue(): void {
        const composer = this.messageInputRef?.nativeElement;
        const value = this.composerValue;
        if (composer && composer.value !== value) {
            composer.value = value;
        }
    }

    // -------------------------------------------------------------------------
    // Voice dictation
    // -------------------------------------------------------------------------

    /** Mic toggle. Also drives the waveform's own (separate) audio stream. */
    onToggleDictation(): void {
        this.dictation.toggle();
    }

    /** Confirm — stops listening and keeps every word, interim included. */
    onConfirmDictation(): void {
        this.dictation.stop();
    }

    /** The one destructive exit: reverts the draft to its pre-dictation text. */
    onCancelDictation(): void {
        this.dictation.cancel();
    }

    onDismissDictationError(): void {
        this.dictation.clearError();
    }

    /**
     * Stop listening but keep the words, for every path that takes the composer
     * off screen — a tab switch, an incoming question card, the drawer closing —
     * or swaps the draft under it (new chat, opening another conversation).
     * The host is mounted at app root and never unmounts, so without this the
     * mic stays hot with no visible field to show what it is hearing.
     */
    private _stopDictationForHiddenComposer(): void {
        if (!this.dictation.listening) {
            return;
        }
        this.dictation.stop();
    }

    /** Keep the level meter's stream in step with the recognition session. */
    private _syncAudioLevels(): void {
        if (this.dictation.listening) {
            this._audioLevels.start();
        } else {
            this._audioLevels.stop();
        }
    }

    /**
     * Set straight on the element from outside Angular: a change-detection pass
     * through this whole template every audio frame is the wrong trade.
     */
    private _paintWaveform(levels: number[]): void {
        const composer = this.messageInputRef?.nativeElement;
        if (composer) {
            composer.levels = levels;
        }
    }

    onStopStream(): void {
        this._drawer.cancelStream();
    }

    // -------------------------------------------------------------------------
    // PDF attachment (SD-9309)
    // -------------------------------------------------------------------------

    private _refreshComposerAttachments(): void {
        const staged = this.pendingAttachments.map((a) => {
            const preview = a.uploadedFileId ? this._imagePreviews.get(a.uploadedFileId) : undefined;
            return {
                id: gableAttachmentKey(a),
                name: a.fileName,
                meta: (a.fileName.split('.').pop() || '').toUpperCase(),
                kind: preview ? 'image' as const : 'file' as const,
                previewUrl: preview,
                documentId: a.documentId ? String(a.documentId) : undefined,
            };
        });
        this.composerAttachments = [...staged, ...this.attachmentUploads];
        this._cdr.markForCheck();
    }

    get composerMeta(): string {
        const n = this.composerValue.length;
        return n >= 9000 ? `${n.toLocaleString()} / 10,000` : '';
    }

    onAttachClick(): void {
        this.attachmentFileInputRef?.nativeElement.click();
    }

    onAttachImageClick(): void {
        this.attachmentImageInputRef?.nativeElement.click();
    }

    onAttachmentFileSelected(event: Event, kind: 'file' | 'image' = 'file'): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        input.value = '';
        files.forEach((file) => this._uploadAttachment(file, kind));
    }

    onComposerAttachDocuments(docs: GableLibraryDocument[]): void {
        this._drawer.addPendingAttachments(docs.map((d) => ({ documentId: Number(d.id), fileName: d.name })));
    }

    onComposerRemoveAttachment(id: string): void {
        if (this.attachmentUploads.some((u) => u.id === id)) {
            this.attachmentUploads = this.attachmentUploads.filter((u) => u.id !== id);
            this._refreshComposerAttachments();
            return;
        }
        this._releasePreview(id);
        this._drawer.removePendingAttachment(id);
    }

    onComposerClearAttachments(): void {
        this.pendingAttachments.forEach((a) => this._releasePreview(gableAttachmentKey(a)));
        this.attachmentUploads = this.attachmentUploads.filter((u) => u.status === 'uploading');
        this._refreshComposerAttachments();
        this._drawer.clearPendingAttachments();
    }

    private _uploadAttachment(file: File, kind: 'file' | 'image'): void {
        const id = `pending-${++this._uploadSeq}`;
        const invalid = this._attachmentUploader.validateFile(file);
        if (invalid) {
            this.attachmentUploads = [...this.attachmentUploads, { id, name: file.name, status: 'error', error: invalid }];
            this._refreshComposerAttachments();
            return;
        }
        this.attachmentUploads = [...this.attachmentUploads, { id, name: file.name, kind, status: 'uploading' }];
        this._refreshComposerAttachments();
        this._attachmentUploader.uploadFile(file).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.attachmentUploads = this.attachmentUploads.filter((u) => u.id !== id);
                this._refreshComposerAttachments();
                if (file.type.startsWith('image/')) {
                    this._imagePreviews.set(res.uploadedFileId, URL.createObjectURL(file));
                }
                this._drawer.addPendingAttachments([{ uploadedFileId: res.uploadedFileId, fileName: res.fileName }]);
                this._cdr.markForCheck();
            },
            error: (err) => {
                this.attachmentUploads = this.attachmentUploads.map((u) => (u.id === id
                    ? { id, name: file.name, status: 'error' as const, error: err?.message ?? 'Could not upload' }
                    : u));
                this._refreshComposerAttachments();
            },
        });
    }

    /** Best effort, once per org: an admin without Documents access just gets a menu without the library. */
    private _loadDocumentLibrary(): void {
        const orgId = LegFiJwtService.read()?.orgId ?? null;
        if (!orgId || this._documentLibraryOrgId === orgId || this._scope.isPmcScoped) {
            return;
        }
        this._documentLibraryOrgId = orgId;
        this._documents.getFlatDocumentsForOrganization(orgId).pipe(
            catchError(() => of([])),
            untilDestroyed(this),
        ).subscribe((files) => {
            const docs = toLibraryDocuments((files as Object[]).map((f) => new OrganizationFile(f)));
            this.documentCategories = topCategories(docs, PICKER_CATEGORY_LIMIT);
            const shelves = new Set(this.documentCategories);
            this.documentLibrary = docs.map((d) => (d.category && shelves.has(d.category) ? d : { ...d, category: undefined }));
            this._cdr.markForCheck();
        });
    }

    private _releasePreviewsNotIn(kept: GableChatAttachment[]): void {
        const keep = new Set(kept.map((a) => a.uploadedFileId).filter((id): id is number => !!id));
        [...this._imagePreviews.keys()].filter((id) => !keep.has(id)).forEach((id) => {
            URL.revokeObjectURL(this._imagePreviews.get(id)!);
            this._imagePreviews.delete(id);
        });
    }

    private _releasePreview(key: string): void {
        const match = /^upload-(\d+)$/.exec(key);
        const url = match ? this._imagePreviews.get(Number(match[1])) : undefined;
        if (url) {
            URL.revokeObjectURL(url);
            this._imagePreviews.delete(Number(match![1]));
        }
    }

    onSend(): void {
        // `capture()` before reading anything: `recognition.stop()` finalizes
        // asynchronously, so stopping and then reading `draftMessage` would
        // drop the half-sentence still in flight. ph-composer's Enter routes here too.
        const text = this._captureComposerText();
        if ((!text && !this.pendingAttachments.length) || this.attachmentUploads.some((u) => u.status === 'uploading') || (this.isSending && !this.backgroundMode)) {
            return;
        }
        const pendingCardId = AFFIRMATIONS.has(text.toLowerCase().replace(/[.!]+$/, '')) && !this.pendingAttachments.length ? this._latestPendingCardId() : null;
        if (pendingCardId !== null) {
            this._drawer.setDraft('');
            this._focusPendingCard(pendingCardId);
            return;
        }
        if (this.attachmentUploads.length) {
            this.attachmentUploads = [];
            this._refreshComposerAttachments();
        }
        this._noteIgnoredFeedbackAsk();
        this._drawer.sendMessage(text);
    }

    /**
     * Each ask on screen is the response rate's denominator; an ignored one leaves no vote behind.
     * Only the chat tab renders the thread, and older answers can carry an ask too (SD-10800).
     */
    private _reportShownFeedbackAsk(): void {
        if (!this.isOpen || this.activeTab !== 'chat' || this.hasPendingSandboxCard) {
            return;
        }
        const key = `${threadKey(this.messages)}|${this._votesVersion}`;
        if (this._askScan?.messages === this.messages && this._askScan.key === key) {
            return;
        }
        this._askScan = { messages: this.messages, key };
        this.messages.forEach((msg, index) => {
            const interactionId = msg.interactionId;
            if (interactionId === undefined || this._askReported.has(interactionId) || !this.shouldAskForFeedback(msg, index)) {
                return;
            }
            this._askReported.add(interactionId);
            this._drawer.recordFeedbackAsked(interactionId)
                .pipe(untilDestroyed(this))
                .subscribe({ error: () => undefined });
        });
    }

    /** Moving on past an ask without voting counts towards pausing the asks. */
    private _noteIgnoredFeedbackAsk(): void {
        const index = this.messages.map((m) => m.role).lastIndexOf('assistant');
        if (index >= 0 && !this.hasPendingSandboxCard && this.shouldAskForFeedback(this.messages[index], index)) {
            this._feedbackAskMemory.noteIgnored(Date.now());
        }
    }

    private _latestPendingCardId(): number | null {
        const last = [...this.messages].reverse().find((m) => m.role === 'assistant');
        const cards = last?.toolCards ?? [];
        for (let c = cards.length - 1; c >= 0; c -= 1) {
            const payload = this.asActionPreviewPayload(cards[c]) ?? this.asCrudPreviewPayload(cards[c]);
            if (payload?.state === 'pending') {
                return payload.proposal_id;
            }
        }
        return null;
    }

    private _focusPendingCard(proposalId: number): void {
        this.attentionProposalId = proposalId;
        this._cdr.markForCheck();
        this._host.nativeElement.querySelector(`[data-proposal-id="${proposalId}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (this._attentionTimer) {
            clearTimeout(this._attentionTimer);
        }
        this._attentionTimer = setTimeout(() => {
            this.attentionProposalId = null;
            this._attentionTimer = null;
            this._cdr.markForCheck();
        }, ATTENTION_MS);
    }

    /** The draft as it stands, finalizing any in-flight dictation first. */
    private _captureComposerText(): string {
        const text = this.dictation.listening ? this.dictation.capture() : this.draftMessage ?? '';
        return text.trim();
    }

    /** The running turn keeps working in its conversation; this question gets a fresh one. */
    askInNewChat(): void {
        const text = this._captureComposerText();
        if (!text) {
            return;
        }
        this._drawer.startNewChatWithMessage(text);
    }

    /** Thought steps for ph-chat-reasoning: narration lines as they are, repeated tool calls folded into one with a count. */
    alertStatus(alert: 'success' | 'warn' | 'info'): 'success' | 'warning' | 'information' {
        return alert === 'warn' ? 'warning' : alert === 'info' ? 'information' : 'success';
    }

    reasoningSteps(msg: GableChatMessage): GableReasoningStep[] {
        // Thoughts are replaced, never mutated, so the array itself is the cache key.
        const thoughts = msg.thoughts ?? NO_THOUGHTS;
        let steps = REASONING_STEPS.get(thoughts);
        if (!steps) {
            steps = this._buildReasoningSteps(thoughts);
            REASONING_STEPS.set(thoughts, steps);
        }
        return steps;
    }

    private _buildReasoningSteps(thoughts: GableThought[]): GableReasoningStep[] {
        let steps: { label: string; count: number; kind: string }[] = [];
        for (const thought of thoughts) {
            const kind = thought.kind ?? 'tool';
            const label = kind === 'narration' ? thought.label.trim() : thought.label.replace(/[:\s]+$/, '');
            const last = steps[steps.length - 1];
            steps = kind === 'tool' && last?.kind === 'tool' && last.label === label
                ? [...steps.slice(0, -1), { ...last, count: last.count + 1 }]
                : [...steps, { label, count: 1, kind }];
        }
        return steps.map((step) => step.kind === 'narration'
            ? { label: step.label, narration: true }
            : { label: `${step.label}…${step.count > 1 ? ' × ' + step.count : ''}` });
    }

    hasAnswerText(msg: GableChatMessage): boolean {
        return (msg.content ?? '').trim().length > 0;
    }

    currentStepLabel(msg: GableChatMessage): string {
        const steps = this.reasoningSteps(msg).filter((step) => !step.narration);
        return steps.length ? steps[steps.length - 1].label : 'Thinking…';
    }

    get suggestionItems(): { value: string; label: string }[] {
        return this.currentSuggestions.map((s, i) => ({ value: String(i), label: s.label }));
    }

    get welcomeModules(): { value: string; label: string; icon: string; count: number }[] {
        return this.moduleLinks.map((link) => ({
            value: link.module,
            label: link.label,
            icon: link.icon.replace(/^ph-/, ''),
            count: link.count,
        }));
    }

    get apiSuggestionItems(): { value: string; label: string }[] {
        return this.apiSuggestions.map((text) => ({
            value: text,
            label: text,
        }));
    }

    get communityItems(): { value: string; label: string; icon: string }[] {
        return this.communityResults.map((org) => ({ value: String(org.id), label: org.name, icon: 'buildings' }));
    }

    onSuggestionSelect(value: string): void {
        const suggestion = this.currentSuggestions[Number(value)];
        if (suggestion) {
            this.onSuggestionClick(suggestion);
        }
    }

    onCommunitySelect(value: string): void {
        const org = this.communityResults.find((o) => String(o.id) === value);
        if (org) {
            this.onPickCommunity(org);
        }
    }

    onSuggestionClick(suggestion: GableSuggestion): void {
        if (this.isSending) {
            return;
        }
        // A suggestion sends its own text, not the draft — but leaving the mic
        // hot would have it dictating into the next turn's composer.
        this._stopDictationForHiddenComposer();
        this._drawer.sendMessage(suggestion.prompt);
    }

    /**
     * Click on an AI-supplied follow-up suggestion — sends the suggestion
     * text as the next user message. Mirrors the empty-state suggestion
     * click but takes a plain string (`apiSuggestions` is `string[]`).
     */
    onApiSuggestionClick(text: string): void {
        if (this.isSending) {
            return;
        }
        const trimmed = (text ?? '').trim();
        if (trimmed === '') {
            return;
        }
        this._stopDictationForHiddenComposer();
        this._drawer.sendMessage(trimmed);
    }

    /**
     * Render the follow-up suggestion strip only under the most recent
     * assistant bubble (the Figma's "↳ Schedule for tomorrow / ↳ Add SMS
     * / ↳ Escalate to First Warning" rows). Returns true only when the
     * given message index is the last assistant message AND:
     *   - its stream is complete (don't show suggestions while typing)
     *   - it didn't emit an action-preview card. Suggestions on top of
     *     a still-pending action create the "I already approved" confusion
     *     (a suggestion that says "Switch to Final Notice" gets fired as
     *     a fresh user turn, which the model interprets as a new request
     *     instead of editing the current proposal). Action-preview cards
     *     own their own affordances — Edit, status dropdown, Add unit —
     *     so the follow-up strip is dead weight there.
     */
    isLastAssistantMessage(index: number): boolean {
        const msg = this.messages[index];
        if (!msg || msg.role !== 'assistant') {
            return false;
        }
        if (msg.streamState && msg.streamState !== 'complete') {
            return false;
        }
        if (this._hasActionPreviewCard(msg)) {
            return false;
        }
        for (let i = this.messages.length - 1; i > index; i -= 1) {
            if (this.messages[i].role === 'assistant') {
                return false;
            }
        }
        return true;
    }

    private _hasActionPreviewCard(msg: GableChatMessage): boolean {
        return messageCarriesProposal(msg.toolCards);
    }

    // -------------------------------------------------------------------------
    // Streaming bubble helpers (mirrors the Gable page's template hooks)
    // -------------------------------------------------------------------------

    trackByIndex(index: number): number {
        return index;
    }

    trackByProposalId(_: number, p: GablePendingProposal): number {
        return p.id;
    }

    trackByCitation(_: number, c: GableCitation): string {
        return `${c.table}:${c.id}`;
    }

    trackByToolCard(index: number, _card: GableToolCard): number {
        return index;
    }

    asReportCardPayload(card: GableToolCard): GenerateReportCardPayload | null {
        if (card.tool !== 'generate_report') {
            return null;
        }
        if (!this._payloadMatches(card, isGenerateReportCardPayload)) {
            return null;
        }
        return card.payload;
    }

    asBudgetRedirectPayload(card: GableToolCard): BudgetRedirectCardPayload | null {
        if (!isRedirectCardTool(card.tool)) {
            return null;
        }
        if (!this._payloadMatches(card, isBudgetRedirectCardPayload)) {
            return null;
        }
        return card.payload;
    }

    asActionPreviewPayload(card: GableToolCard): ActionPreviewCardPayload | null {
        if (card.tool !== 'propose_action') {
            return null;
        }
        if (!this._payloadMatches(card, isActionPreviewCardPayload)) {
            return null;
        }
        return card.payload;
    }

    /**
     * A change drafted through the generic write lane. Same card as a curated action, approved
     * through the CRUD endpoint rather than the typed-action commit path.
     */
    asCrudPreviewPayload(card: GableToolCard): ActionPreviewCardPayload | null {
        if (card.tool !== 'propose_crud_change') {
            return null;
        }
        if (!this._payloadMatches(card, isActionPreviewCardPayload)) {
            return null;
        }
        return card.payload;
    }

    onCrudPreviewConfirm(event: { proposalId: number }): void {
        const idempotencyKey = this._randomUuid();
        this._drawer
            .approveProposal(event.proposalId, idempotencyKey)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // `res.result`, not the whole body: core hangs `record_link` off the result
                    // envelope, so passing the outer response buried it a level deeper than the
                    // card reads and every CRUD commit lost its link.
                    this._drawer.patchActionPreviewState(event.proposalId, 'committed', res?.result ?? null);
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] approve crud proposal failed', err);
                    const card = this._findActionPreviewCard(event.proposalId);
                    if (card) {
                        card.submitting = false;
                    }
                    this._retireRefusedCard(event.proposalId, err);
                    this._growler.oops(gableCommitFailureMessage(err));
                    this._cdr.markForCheck();
                },
            });
    }

    asDeliverableCardPayload(card: GableToolCard): DeliverableCardPayload | null {
        if (!isDeliverableCardTool(card.tool)) {
            return null;
        }
        if (!this._payloadMatches(card, isDeliverableCardPayload)) {
            return null;
        }
        return card.payload;
    }

    asLedgerIntelligencePayload(card: GableToolCard): LedgerIntelligenceResult | null {
        if (card.tool !== 'analyze_ledger') {
            return null;
        }
        if (!this._payloadMatches(card, isLedgerIntelligenceCardPayload)) {
            return null;
        }
        return card.payload.result;
    }

    /** Shape check once per payload object, not per change-detection pass; callers gate on `card.tool` first. */
    private _payloadMatches<T>(card: GableToolCard, guard: (p: unknown) => p is T): card is GableToolCard & { payload: T } {
        const payload: unknown = card.payload;
        const cacheable = typeof payload === 'object' && payload !== null;
        let verdict = cacheable ? PAYLOAD_VERDICTS.get(payload) : undefined;
        if (verdict === undefined) {
            verdict = guard(payload);
            if (cacheable) {
                PAYLOAD_VERDICTS.set(payload, verdict);
            }
            if (!verdict) {
                console.warn(`[gable-drawer-host] Skipping tool card with tool="${card.tool}": payload shape mismatch.`);
            }
        }
        return verdict;
    }

    onActionPreviewConfirm(event: { proposalId: number; edits: ActionPreviewEdits }): void {
        const idempotencyKey = this._randomUuid();
        this._drawer
            .commitActionProposal(event.proposalId, idempotencyKey, event.edits)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // Patch the toolCard payload so the card re-renders
                    // into its "committed" final state via ngOnChanges,
                    // independent of any ViewChildren lookup timing. Carry
                    // the execute() result through so the collapsed view
                    // can show counts / deeplink to the destination.
                    this._drawer.patchActionPreviewState(
                        event.proposalId,
                        'committed',
                        res.result ?? null,
                    );
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] commit action failed', err);
                    const card = this._findActionPreviewCard(event.proposalId);
                    if (card) {
                        card.submitting = false;
                    }
                    this._retireRefusedCard(event.proposalId, err);
                    // The chat-origin card had the same silent failure as the
                    // review queue — console only, nothing for the user, so a refused
                    // commit looked like a dead button here too.
                    this._growler.oops(gableCommitFailureMessage(err));
                    this._cdr.markForCheck();
                },
            });
    }

    onActionPreviewCancel(event: { proposalId: number }): void {
        this._drawer
            .rejectProposal(event.proposalId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] reject failed', err);
                    // Even on a server error, flip the card visually so
                    // the user gets feedback. Backend may have already
                    // recorded the proposal as rejected; worst case it's
                    // a stale "pending" row but the user moved on.
                    if (isGableSkippedRefusal(err)) {
                        this._retireRefusedCard(event.proposalId, err);
                    } else {
                        this._drawer.patchActionPreviewState(event.proposalId, 'cancelled');
                    }
                    this._cdr.markForCheck();
                },
            });
    }

    /** A terminal refusal retires the card; a skipped one (409, a stale tab) also refreshes the queue it may sit in. */
    private _retireRefusedCard(proposalId: number, err: unknown): void {
        if (isGableSkippedRefusal(err)) {
            this._drawer.patchActionPreviewState(proposalId, 'skipped');
            this._drawer.loadPendingProposals();
        } else if (isGableTerminalRefusal(err)) {
            this._drawer.patchActionPreviewState(proposalId, 'cancelled');
        }
    }

    onActionPreviewUtility(event: {
        proposalId: number;
        utilityActionId: string;
        edits: ActionPreviewEdits;
    }): void {
        // `add_unit` is handled inline in the recipients section now —
        // the picker expands within the card and emits an `(edit)` patch
        // with `recipients.added_ids` directly. Nothing reaches this
        // handler with that id anymore. Other utility ids
        // (restore_skipped, future utility buttons) flow through.
        this._postRefine(event.proposalId, event.utilityActionId, event.edits);
    }

    private _postRefine(proposalId: number, utilityActionId: string, edits: ActionPreviewEdits): void {
        this._drawer
            .refineActionProposal(proposalId, utilityActionId, edits)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    // Patch the card payload in place inside the assistant
                    // message's toolCards. Backend has rewritten preview +
                    // edits — re-renders flow through the OnPush card.
                    this._drawer.patchActionPreview(res.proposalId, {
                        preview: res.preview,
                        edits: res.edits,
                    });
                    this._cdr.markForCheck();
                },
                error: (err) => {
                    console.error('[gable-drawer-host] refine failed', err);
                },
            });
    }

    private _findActionPreviewCard(proposalId: number): GableActionPreviewCardComponent | undefined {
        return this.actionPreviewCardsRef?.find((c) => {
            const p = c.payload as unknown as Record<string, unknown> | null;
            return (p?.['proposal_id'] ?? p?.['proposalId']) === proposalId;
        });
    }

    private _randomUuid(): string {
        // crypto.randomUUID is widely supported in evergreen browsers.
        // Fallback to a timestamp+random string in the rare unsupported case.
        const c = window.crypto;
        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    elapsedSeconds(msg: GableChatMessage): number | null {
        if (!msg.streamStartedAt || !msg.streamEndedAt) {
            return null;
        }
        return Math.max(1, Math.round((msg.streamEndedAt - msg.streamStartedAt) / 1000));
    }

    /**
     * True once the streaming answer has begun rendering a real, block-level
     * result (a markdown table or a list) — at which point the bottom-of-turn
     * "working" dots are retired, because the visible result is now the live
     * element. Plain prose alone does NOT count: preamble sentences and
     * quiet gaps between tool calls should keep the heartbeat alive.
     */
    isRenderingResults(content: string | null | undefined): boolean {
        if (!content) {
            return false;
        }
        // Markdown table row (`| … |`), a bullet/numbered list item at line
        // start, or a completed gable-suggestions card block (the card pops
        // in whole once its closing fence arrives).
        return /(^|\n)\s*\|.*\|/.test(content)
            || /(^|\n)\s*(?:[-*]|\d+\.)\s+\S/.test(content)
            || /```gable-suggestions[\s\S]*?```/.test(content);
    }

    // -------------------------------------------------------------------------
    // Question card
    // -------------------------------------------------------------------------

    onAnswers(answers: GableQuestionAnswer[]): void {
        const batch = this.pendingQuestions;
        if (!batch) {
            return;
        }
        const summary = this._summarizeAnswers(batch, answers);
        this._drawer.answerQuestions(answers, summary);
    }

    onCloseQuestionCard(): void {
        this._drawer.closeQuestionCard();
    }

    get submittingAnswers(): boolean {
        return this._drawer.submittingAnswers;
    }

    // -------------------------------------------------------------------------
    // Message actions (copy)
    // -------------------------------------------------------------------------

    /** Strips the suggestions block exactly as the bubble's own binding does. */
    private readonly _suggestions = new GableSuggestionsPipe();

    /**
     * Transient per-message result of the copy button, keyed by messageId.
     * Confirmation lives on the button that was pressed — no toast — so the
     * state has to be per-row rather than a single component-wide flag.
     */
    private readonly _copyState = new Map<number, GableCopyState>();

    /** `'copied' | 'failed'` while the confirmation shows, else `null`. */
    copyState(msg: GableChatMessage): GableCopyState | null {
        const key = this._copyKey(msg);
        return key === undefined ? null : this._copyState.get(key) ?? null;
    }

    /**
     * The clipboard text for a message: the markdown the bubble actually renders,
     * NOT the raw `content`. `content` still carries the fenced
     * ```gable-suggestions {…}``` block, which the pipe strips before display —
     * copying the raw string pastes JSON the user was never shown.
     */
    copyableText(msg: GableChatMessage): string {
        const cached = this._copyableText.get(msg);
        if (cached !== undefined) {
            return cached;
        }
        const text = this._suggestions.transform(msg.content).text.trim();
        this._copyableText.set(msg, text);
        return text;
    }

    /**
     * Memo for `copyableText`, which is reached from a template `*ngIf` and would
     * otherwise re-parse every complete message on every change detection pass —
     * ~10/sec while a turn streams. Keyed on the message OBJECT: every mutation in
     * GableDrawerService replaces the object rather than editing it, so a changed
     * message is a new key and a dropped message is garbage collected with its entry.
     */
    private readonly _copyableText = new WeakMap<GableChatMessage, string>();

    /**
     * Whether the Copy button should exist at all. The message footer is gated on
     * `interactionId`, a feedback-telemetry id that is deliberately moved between
     * rows — so it says nothing about whether there is text to copy. A card-only
     * answer settles with `content: ''` and both ids set; without this check Copy
     * writes an empty string over the user's clipboard and reports "Copied".
     */
    canCopyMessage(msg: GableChatMessage): boolean {
        return this._copyKey(msg) !== undefined && this.copyableText(msg) !== '';
    }

    /** `messageId` is absent on a message pushed without a server round trip (e.g. `_pushFailedMessage`), so fall back to `interactionId`, present wherever the footer renders. */
    private _copyKey(msg: GableChatMessage): number | undefined {
        return msg.messageId ?? msg.interactionId;
    }

    /**
     * Copies the message's plain text — `content` is the markdown source the
     * bubble renders from, which is what a person pasting into an email wants,
     * not the rendered HTML.
     *
     * `navigator.clipboard` throws on insecure origins and when permission is
     * denied, so the failure branch is not theoretical: without it a blocked
     * clipboard reads as a dead button.
     */
    /**
     * Confirmation state from the shared `CopyToClipboardDirective` in `inline`
     * mode: the directive owns the clipboard write, the empty-value refusal and
     * the reset timer, and reports 'copied' / 'failed' and then null.
     *
     * The state still lives in a map here rather than on the button: the footer
     * renders per message, and each message's confirmation is its own.
     */
    onCopyStateChange(msg: GableChatMessage, state: GableCopyState | null): void {
        const key = this._copyKey(msg);
        if (key === undefined) {
            return;
        }
        if (state === null) {
            this._copyState.delete(key);
        } else {
            this._copyState.set(key, state);
        }
        this._cdr.markForCheck();
    }


    // -------------------------------------------------------------------------
    // Feedback
    // -------------------------------------------------------------------------

    getFeedback(messageId: number | undefined): GableFeedback | null {
        return this._drawer.getFeedback(messageId);
    }

    onFeedback(msg: GableChatMessage, feedback: 'good' | 'bad'): void {
        const messageId = msg.messageId;
        const interactionId = msg.interactionId;
        if (messageId === undefined || interactionId === undefined) {
            return;
        }
        const source = this.shouldAskForFeedback(msg, this.messages.indexOf(msg)) ? 'asked' : 'unprompted';
        const previous = this._drawer.getFeedback(messageId) ?? 'none';
        const next: GableFeedback = previous === feedback ? 'none' : feedback;
        this._drawer.setFeedback(messageId, next);
        this._votesVersion += 1;
        if (next === 'none') {
            this._votedThisSession.delete(messageId);
        } else {
            this._votedThisSession.add(messageId);
            this._feedbackAskMemory.noteVoted();
        }
        this._drawer.recordFeedback(interactionId, next, source)
            .pipe(untilDestroyed(this))
            .subscribe({
                error: () => this._drawer.setFeedback(messageId, previous),
            });

        // Thumbs-down expands the inline feedback panel (reason chips +
        // Send path). Thumbs-up or unsetting collapses it.
        if (feedback === 'bad' && next === 'bad') {
            this._feedbackPanelOpenFor = messageId;
        } else {
            this._feedbackPanelOpenFor = null;
        }
        this._cdr.markForCheck();
    }

    /**
     * Ask for a rating after a decline or after a confirmed write, until the user votes (SD-10800),
     * and while the feedback campaign runs, on a sample of substantive answers too.
     */
    shouldAskForFeedback(msg: GableChatMessage, index: number): boolean {
        if (msg.role !== 'assistant' || msg.streamState !== 'complete' || msg.interactionId === undefined) {
            return false;
        }
        if (this._hasVote(msg) || this.wasFeedbackSent(msg)) {
            return false;
        }
        return this._isDecline(msg) || this._followsCommittedCard(index) || this._campaignAsks(index);
    }

    /** The thank-you that replaces the ask once the reader votes, unless the reasons panel is taking over. */
    showFeedbackThanks(msg: GableChatMessage): boolean {
        return msg.messageId !== undefined
            && this._votedThisSession.has(msg.messageId)
            && this._hasVote(msg)
            && !this.isFeedbackPanelOpenFor(msg.messageId)
            && !this.wasFeedbackSent(msg);
    }

    private _hasVote(msg: GableChatMessage): boolean {
        return (this.getFeedback(msg.messageId) ?? 'none') !== 'none';
    }

    private _campaignAsks(index: number): boolean {
        return this._feedbackCampaignOn === true
            && !this._feedbackAskMemory.isPaused(Date.now())
            && campaignAsksFor(this.messages, index, (m) => this._hasVote(m) || this.wasFeedbackSent(m));
    }

    private _isDecline(msg: GableChatMessage): boolean {
        const usedTools = (msg.toolsUsed?.length ?? 0) > 0
            || (msg.toolCards?.length ?? 0) > 0
            || (msg.thoughts ?? []).some((t) => (t.kind ?? 'tool') === 'tool');
        return !usedTools && DECLINE_PATTERN.test((msg.content ?? '').slice(0, 200));
    }

    private _followsCommittedCard(index: number): boolean {
        for (let i = index - 1; i >= 0; i -= 1) {
            const prev = this.messages[i];
            if (prev.role !== 'assistant') {
                continue;
            }
            return (prev.toolCards ?? []).some((card) => {
                const payload = this.asActionPreviewPayload(card) ?? this.asCrudPreviewPayload(card);
                return payload?.state === 'committed';
            });
        }
        return false;
    }

    /** Whether the inline feedback panel is open for a given message. */
    isFeedbackPanelOpenFor(messageId: number | undefined): boolean {
        return messageId !== undefined && this._feedbackPanelOpenFor === messageId;
    }

    /** Cancel-all-the-panels helper (Skip path). */
    closeFeedbackPanel(): void {
        this._feedbackPanelOpenFor = null;
        this._cdr.markForCheck();
    }

    // --- Inline feedback panel event handlers ---

    onChipFeedback(msg: GableChatMessage, event: { chip: GableFeedbackChip; detail?: string }): void {
        const interactionId = msg.interactionId;
        if (interactionId === undefined) { return; }
        this._aiPoc.recordChipFeedback(interactionId, event.chip, event.detail)
            .pipe(untilDestroyed(this))
            .subscribe();
        // Lock further feedback on this interaction and swap the thumbs
        // row out for the green "submitted" confirmation.
        this._drawer.markFeedbackSent(interactionId);
        this.closeFeedbackPanel();
        this._cdr.markForCheck();
    }

    /** Renders the green "feedback submitted" confirmation instead of thumbs. */
    wasFeedbackSent(msg: GableChatMessage): boolean {
        return this._drawer.wasFeedbackSent(msg.interactionId);
    }

    get isSuperUser(): boolean {
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return !!jwt?.superUser;
    }

    /** Core's workflow summary email lands on `?gable=activity`: open the drawer on Activity, then drop the param. */
    private _consumeGableDeepLink(url: string): void {
        if (!url || this._router.parseUrl(url).queryParams['gable'] !== 'activity') {
            return;
        }
        this._drawer.openActivity('all');
        void this._router.navigate([], { queryParams: { gable: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }

    /** Workflows tab audience (SD-9963): mirrors core's GableAccessResolver::canManageOrg() (SD-10198), super or an org admin whose org has the Gable flag. Org-scoped only. */
    get canManageWorkflows(): boolean {
        if (this._scope.isPmcScoped) {
            return false;
        }
        const jwt: JwtLegFiClaims | null = LegFiJwtService.read();
        return !!jwt?.superUser || (!!jwt?.admin && this._drawer.flagEnabled);
    }

    // -------------------------------------------------------------------------
    // Proposals
    // -------------------------------------------------------------------------

    get proposalInFlight(): number | null {
        return this._drawer.proposalInFlight;
    }

    onReviewProposal(proposal: GablePendingProposal): void {
        if (this._drawer.proposalInFlight === proposal.id) {
            return;
        }
        this.reviewProposal = proposal;
        this._cdr.markForCheck();
    }

    /** The proposal the approval dialog is open on, or null while it is closed. */
    reviewProposal: GablePendingProposal | null = null;

    onApprovalClosed(result: ApprovalDialogResult): void {
        const proposal = this.reviewProposal;
        this.reviewProposal = null;
        this._cdr.markForCheck();
        if (!proposal) {
            return;
        }
        if (result === 'approve') {
            this._commitProposal(proposal);
        } else if (result === 'reject') {
            this._rejectProposal(proposal);
        }
    }

    onCancelProposal(proposal: GablePendingProposal): void {
        if (this._drawer.proposalInFlight === proposal.id) {
            return;
        }
        this._rejectProposal(proposal);
    }

    private _commitProposal(proposal: GablePendingProposal): void {
        const idempotencyKey = crypto.randomUUID();
        this._drawer.proposalInFlight = proposal.id;
        this._cdr.markForCheck();
        this._drawer.approveProposal(proposal.id, idempotencyKey)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (res) => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.removeProposal(proposal.id);
                    const friendly = this._friendlyActionDescription(proposal);
                    const note = res.idempotent
                        ? `${friendly} — already done previously, no duplicate created.`
                        : `${friendly}.`;
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: note,
                        timestamp: new Date(),
                        alert: 'success',
                    });
                },
                error: () => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: 'Sorry — committing that proposal failed. It may have expired or been rejected by another tab. Try again, or ask the assistant to repropose.',
                        timestamp: new Date(),
                    });
                },
            });
    }

    private _rejectProposal(proposal: GablePendingProposal): void {
        this._drawer.proposalInFlight = proposal.id;
        this._cdr.markForCheck();
        this._drawer.rejectProposal(proposal.id)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this._drawer.proposalInFlight = null;
                    this._drawer.removeProposal(proposal.id);
                    this._drawer.appendMessage({
                        role: 'assistant',
                        content: `Cancelled the ${proposal.operation} on ${proposal.modelClass}. Nothing was changed.`,
                        timestamp: new Date(),
                        alert: 'info',
                    });
                },
                error: () => {
                    this._drawer.proposalInFlight = null;
                    this._cdr.markForCheck();
                },
            });
    }

    private _friendlyActionDescription(proposal: GablePendingProposal): string {
        const verb = {
            create: 'created',
            update: 'updated',
            delete: 'deleted',
        }[proposal.operation] ?? proposal.operation;
        const payload = proposal.payload ?? {};
        const primary = (payload['name'] ?? payload['title'] ?? null) as string | null;
        const friendlyModel = proposal.modelClass === 'ViolationStatement' ? 'Violation' : proposal.modelClass;
        if (primary) {
            return `${friendlyModel} "${primary}" ${verb}`;
        }
        if (proposal.entityId !== null) {
            return `${friendlyModel} #${proposal.entityId} ${verb}`;
        }
        return `${friendlyModel} ${verb}`;
    }

    private _summarizeAnswers(
        batch: GablePendingQuestions,
        answers: GableQuestionAnswer[],
    ): string {
        const byId = new Map(batch.questions.map((q) => [q.id, q]));
        let parts: string[] = [];
        for (const a of answers) {
            const q = byId.get(a.id);
            if (!q) {
                continue;
            }
            const label = q.prompt.replace(/[?:]+\s*$/, '');
            const optById = new Map((q.options ?? []).map((o) => [o.value, o.label]));
            let valueText: string;
            if (Array.isArray(a.value)) {
                valueText = a.value
                    .map((v) => optById.get(String(v)) ?? String(v))
                    .filter((v) => v !== '')
                    .join(', ');
            } else if (typeof a.value === 'number') {
                valueText = String(a.value);
            } else {
                valueText = optById.get(a.value) ?? a.value;
            }
            if (valueText !== '') {
                parts = [...parts, `${label}: ${valueText}`];
            }
        }
        return parts.join(' · ');
    }
}
